// ANANT CITYPRO CONNECT — Hyper-local, offline-first PWA MVP
// Multi-role: Customer · Provider · Cash Center · Admin
// All prices in AT · 33% CRR cascade on completion via backend distribute_fee
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  MapPin, Loader2, ChevronRight, Sparkles, Users, Wallet, Package,
  Send, CheckCircle2, XCircle, Cloud, PhoneCall, ArrowUpDown, Clock,
} from 'lucide-react';
import ATWalletBadge from '../../components/ATWalletBadge';
import ATPayCard from '../../components/ATPayCard';
import api from '../../utils/api';
import { fmtDate, fmtDateTime, fmtDateHuman } from '../../utils/dateFmt';

const LS_QUEUE = 'citypro_offline_queue';
const loadQ = () => { try { return JSON.parse(localStorage.getItem(LS_QUEUE) || '[]'); } catch { return []; } };
const saveQ = (q) => localStorage.setItem(LS_QUEUE, JSON.stringify(q));

// ============ HOLD-TO-SPEAK — voice-fills order form (Hinglish + Hindi) ============
// Usage: <HoldToSpeak onTranscript={(text) => parseIntoForm(text)} />
function HoldToSpeak({ onTranscript, disabled }) {
  const [listening, setListening] = React.useState(false);
  const [transcript, setTranscript] = React.useState('');
  const recRef = React.useRef(null);
  const activeRef = React.useRef(false);
  const start = () => {
    if (disabled || activeRef.current) return;
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast.error('🎤 Voice not supported (Chrome/Edge/Safari me hi chalega)'); return; }
    try {
      const rec = new SR();
      rec.lang = 'hi-IN'; rec.interimResults = true; rec.continuous = true;
      let finalT = '';
      rec.onstart = () => { activeRef.current = true; setListening(true); setTranscript(''); };
      rec.onresult = (e) => {
        // Rebuild finals from scratch each event — Web Speech in Chrome re-fires
        // the same result index when it upgrades an interim to final. If we just
        // accumulated (+=), we'd double every segment. Rebuilding avoids duplication.
        let final = ''; let interim = '';
        for (let i = 0; i < e.results.length; i++) {
          const t = e.results[i][0].transcript;
          if (e.results[i].isFinal) final += t + ' '; else interim += t;
        }
        finalT = final.trim();
        setTranscript((finalT + ' ' + interim).replace(/\s+/g, ' ').trim());
      };
      rec.onerror = (ev) => { activeRef.current = false; setListening(false); if (ev.error !== 'aborted') toast.error(`🎤 ${ev.error}`); };
      rec.onend = () => {
        activeRef.current = false; setListening(false);
        const text = (finalT || '').trim();
        if (text) onTranscript?.(text); else if (!finalT) toast.error('Kuch bola nahi 🤔');
      };
      recRef.current = rec; rec.start();
    } catch (e) { toast.error('🎤 Voice start failed'); }
  };
  const stop = () => { try { recRef.current?.stop(); } catch { /* noop */ } };
  return (
    <div className="flex items-center gap-2" data-testid="cp-hold-to-speak-wrap">
      <button
        type="button"
        onPointerDown={start}
        onPointerUp={stop}
        onPointerLeave={() => { if (listening) stop(); }}
        onContextMenu={(e) => e.preventDefault()}
        disabled={disabled}
        data-testid="cp-voice-hold"
        className={`select-none flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-black shadow-lg transition ${listening ? 'bg-red-500 text-white animate-pulse scale-105' : 'bg-gradient-to-r from-rose-500 to-fuchsia-500 text-white'}`}
      >
        <span className="text-lg leading-none">{listening ? '🔴' : '🎤'}</span>
        {listening ? 'Bolte raho…' : 'Hold to Speak'}
      </button>
      {transcript && (
        <div className="text-[10px] px-2 py-1 rounded bg-slate-800 border border-slate-700 max-w-[220px] truncate italic text-slate-200">"{transcript}"</div>
      )}
    </div>
  );
}

// Smart Hinglish/Hindi parser: extracts pickup, drop, km, notes from voice text.
function parseCityProVoice(raw) {
  const t = (raw || '').toLowerCase().replace(/\s+/g, ' ').trim();
  const out = { pickup_addr: '', drop_addr: '', distance_km: '', notes: '' };
  // km / kilometre
  const kmMatch = t.match(/(\d+(?:\.\d+)?)\s*(?:km|k\.m\.?|kilomet(?:er|re)|किलोमीटर|किमी)/i);
  if (kmMatch) out.distance_km = kmMatch[1];
  // pickup: "pickup from X", "pickup X", "se lena hai X", "X se pickup"
  const pMatch = t.match(/(?:pickup|pick up|pikap|pick|pikkap)\s*(?:from|se|point|pe|par|address|location)?\s*[:,-]?\s*(.+?)(?=\s+(?:drop|drop point|se|par|to|deliver|deliverry|le\s*jao|le\s*jaana|distance|kilometer|km|notes|note|thanks|$))/i);
  if (pMatch) out.pickup_addr = pMatch[1].trim();
  // drop: "drop to X", "drop at X", "X pe drop", "X pe deliver"
  const dMatch = t.match(/(?:drop|deliver|deliverry|le\s*jaana|le\s*jao|to|pahuncha(?:na|do)?)\s*(?:to|at|address|location|point|pe|par|ka|kaha)?\s*[:,-]?\s*(.+?)(?=\s+(?:distance|kilometer|km|notes|note|thanks|$))/i);
  if (dMatch) out.drop_addr = dMatch[1].trim();
  // notes: "notes X", "note X", "extra X", "special X"
  const nMatch = t.match(/(?:notes?|extra|special|dhyaan|dhyan)\s*[:,-]?\s*(.+?)$/i);
  if (nMatch) out.notes = nMatch[1].trim();
  // If nothing matched, dump whole text into pickup_addr as fallback so user can edit
  if (!out.pickup_addr && !out.drop_addr && !out.notes) out.notes = raw.trim();
  return out;
}

// Speak parsed order back to customer in Hindi so they can verify without looking
function speakOrderReadbackHindi(parsed, selected) {
  if (!('speechSynthesis' in window)) return;
  const parts = [];
  parts.push(`आपका ऑर्डर तैयार है ${selected?.name || 'service'} के लिए।`);
  if (parsed.pickup_addr) parts.push(`पिकअप: ${parsed.pickup_addr}।`);
  if (parsed.drop_addr) parts.push(`ड्रॉप: ${parsed.drop_addr}।`);
  if (parsed.distance_km) parts.push(`दूरी: ${parsed.distance_km} किलोमीटर।`);
  if (parsed.notes) parts.push(`नोट: ${parsed.notes}।`);
  parts.push('अगर सही है तो कन्फर्म बटन दबाएँ।');
  const u = new SpeechSynthesisUtterance(parts.join(' '));
  u.lang = 'hi-IN'; u.rate = 0.95; u.pitch = 1.0;
  // Try to pick a Hindi voice if the browser has one
  const voices = window.speechSynthesis.getVoices?.() || [];
  const hi = voices.find((v) => v.lang?.startsWith('hi')) || voices.find((v) => /hindi|india/i.test(v.name || ''));
  if (hi) u.voice = hi;
  window.speechSynthesis.cancel();
  window.speechSynthesis.speak(u);
}

export default function AnantCityPro() {
  const [cfg, setCfg] = useState(null);
  const [tab, setTab] = useState('customer');
  const [loading, setLoading] = useState(true);
  const [online, setOnline] = useState(navigator.onLine);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/citypro/config'); setCfg(r.data); }
    catch { /* silent */ }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const on = () => { setOnline(true); syncOffline(); };
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);

  // Auto-sync offline queue when online
  const syncOffline = async () => {
    const q = loadQ();
    if (q.length === 0) return;
    try {
      const r = await api.post('/citypro/offline-sync', { orders: q });
      const okIds = (r.data?.results || []).filter((x) => x.ok).map((x) => x.offline_id);
      const remaining = q.filter((o) => !okIds.includes(o.offline_id));
      saveQ(remaining);
      if (okIds.length) toast.success(`✓ ${okIds.length} offline orders synced`);
    } catch { /* stay queued */ }
  };
  useEffect(() => { if (online) syncOffline(); /* on mount if online */ }, [online]);

  if (loading || !cfg) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 text-white">
      <Loader2 className="w-8 h-8 animate-spin" />
    </div>
  );

  return (
    <div className="min-h-screen pt-20 bg-gradient-to-b from-emerald-950 via-slate-950 to-cyan-950 text-white">
      <div className="sticky top-16 z-40 bg-slate-950 backdrop-blur-xl border-b-2 border-emerald-400/50 px-4 py-1.5 flex items-center justify-between gap-2 shadow-2xl shadow-black">
        <Link to="/" className="text-xs text-slate-400 hover:text-white shrink-0">← Home</Link>
        <span className={`text-[9px] px-1.5 py-0.5 rounded font-black ${online ? 'bg-emerald-500/25 text-emerald-300' : 'bg-red-500/25 text-red-300'}`}>{online ? '🟢 Online' : '🔴 Offline (queued)'}</span>
        <ATWalletBadge compact />
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6" data-testid="citypro-page">
        {/* Hero */}
        <div className="relative overflow-hidden rounded-3xl border-2 border-emerald-400/50 bg-gradient-to-br from-emerald-900 via-teal-900/60 to-cyan-900 p-5 mb-6" data-testid="cp-hero">
          <div className="absolute -top-12 -right-12 w-64 h-64 rounded-full bg-emerald-500/25 blur-3xl animate-pulse pointer-events-none" />
          <div className="absolute -bottom-12 -left-12 w-64 h-64 rounded-full bg-cyan-500/25 blur-3xl pointer-events-none" />
          <div className="relative flex items-center gap-4">
            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl border-2 border-emerald-400 shadow-xl shadow-emerald-500/50 flex items-center justify-center bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-500 text-4xl">
              🏙️
            </div>
            <div className="flex-1">
              <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/25 border border-emerald-400/40 text-[9px] font-black tracking-widest text-emerald-200 mb-1">
                <Sparkles className="w-3 h-3" /> CITYPRO CONNECT · 15 SERVICES
              </div>
              <h1 className="text-2xl sm:text-3xl font-black tracking-tight leading-tight">Aapka Sheher · Aapki Seva</h1>
              <div className="text-[11px] opacity-80 mt-1">Taxi · Laundry · Health · Retail · 11 Untapped Services · 33% CRR · Offline-First</div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-4 sm:grid-cols-8 gap-1 p-1 rounded-2xl border border-slate-700 bg-slate-900/70 mb-4" data-testid="cp-tabs">
          <TabBtn active={tab === 'customer'} onClick={() => setTab('customer')} testid="cp-tab-customer">🛒 Book</TabBtn>
          <TabBtn active={tab === 'orders'} onClick={() => setTab('orders')} testid="cp-tab-orders">📦 Orders</TabBtn>
          <TabBtn active={tab === 'shops'} onClick={() => setTab('shops')} testid="cp-tab-shops">🏬 Shops</TabBtn>
          <TabBtn active={tab === 'partners'} onClick={() => setTab('partners')} testid="cp-tab-partners">🛵 Partners</TabBtn>
          <TabBtn active={tab === 'provider'} onClick={() => setTab('provider')} testid="cp-tab-provider">🚕 Provider</TabBtn>
          <TabBtn active={tab === 'cash'} onClick={() => setTab('cash')} testid="cp-tab-cash">💸 Cash</TabBtn>
          <TabBtn active={tab === 'wabot'} onClick={() => setTab('wabot')} testid="cp-tab-wabot">💬 WA Bot</TabBtn>
          <TabBtn active={tab === 'profile'} onClick={() => setTab('profile')} testid="cp-tab-profile">⚙️ Profile</TabBtn>
        </div>

        {tab === 'customer' && <><ATPayCard label="AT Pay Wallet · CityPro" /><div className="mt-3" /><CustomerTab cfg={cfg} online={online} onReload={load} /></>}
        {tab === 'orders' && <OrderBookTab />}
        {tab === 'shops' && <ShopsTab cfg={cfg} />}
        {tab === 'partners' && <DeliveryPartnersTab cfg={cfg} />}
        {tab === 'provider' && <ProviderTab cfg={cfg} onReload={load} />}
        {tab === 'cash' && <CashCenterTab cfg={cfg} />}
        {tab === 'wabot' && <WhatsAppBotTab />}
        {tab === 'profile' && <ProfileTab cfg={cfg} onReload={load} />}

        <div className="mt-8 rounded-2xl border border-slate-800 bg-slate-900/60 p-3 text-[10px] text-slate-400 leading-relaxed">
          <b className="text-slate-200">CityPro Legal:</b> AT is a closed-loop discount utility & promotional affiliate rebate (non-VDA). Services offered by independent providers; platform is a listing facilitator. All bookings are contract-based; cancellations refund AT to your wallet. 33% CRR on every completed order flows through your 7-level upline. Offline orders queue locally and auto-sync when internet returns.
        </div>
      </div>
    </div>
  );
}

const TabBtn = ({ active, onClick, testid, children }) => (
  <button onClick={onClick} data-testid={testid}
    className={`text-[10px] sm:text-xs px-1 py-2 rounded-xl font-black transition ${active ? 'bg-gradient-to-r from-emerald-500 to-cyan-500 text-black shadow-lg' : 'text-slate-300 hover:bg-slate-800'}`}>
    {children}
  </button>
);

// ============ CUSTOMER — book services ============
function CustomerTab({ cfg, online }) {
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ pickup_addr: '', drop_addr: '', distance_km: '', notes: '', provider_id: '' });
  const [providers, setProviders] = useState([]);
  const [nearby, setNearby] = useState(null); // { providers, center, radius_km }
  const [orders, setOrders] = useState([]);
  const [busy, setBusy] = useState(false);
  const [rating, setRating] = useState(null); // { order_id }

  const loadOrders = useCallback(async () => {
    if (!online) return;
    try { const r = await api.get('/citypro/orders', { params: { role: 'customer', limit: 30 } }); setOrders(r.data?.orders || []); }
    catch { /* silent */ }
  }, [online]);
  useEffect(() => {
    loadOrders();
    const t = setInterval(loadOrders, 20000);
    const h = () => loadOrders();
    window.addEventListener('citypro-refresh', h);
    return () => { clearInterval(t); window.removeEventListener('citypro-refresh', h); };
  }, [loadOrders]);

  useEffect(() => {
    if (!selected || !online) { setProviders([]); setNearby(null); return; }
    api.get('/citypro/providers', { params: { role: selected.provider_role, limit: 20 } })
      .then((r) => setProviders(r.data?.providers || [])).catch(() => setProviders([]));
    // Live map only for mobility services
    if (selected.provider_role.startsWith('driver_') && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((pos) => {
        api.get('/citypro/providers/nearby', { params: { role: selected.provider_role, lat: pos.coords.latitude, lng: pos.coords.longitude, radius_km: 5 } })
          .then((r) => setNearby(r.data)).catch(() => setNearby(null));
      }, () => setNearby(null), { timeout: 5000 });
    }
  }, [selected, online]);

  const estPrice = useMemo(() => {
    if (!selected) return 0;
    let p = selected.base_at;
    if (selected.per_km && form.distance_km) p += selected.per_km * parseFloat(form.distance_km);
    return Math.round(p * 100) / 100;
  }, [selected, form.distance_km]);

  const book = async () => {
    if (!selected) return;
    setBusy(true);
    const body = {
      service_id: selected.id,
      provider_id: form.provider_id || null,
      pickup_addr: form.pickup_addr || null,
      drop_addr: form.drop_addr || null,
      distance_km: form.distance_km ? parseFloat(form.distance_km) : null,
      notes: form.notes || null,
      offline_id: crypto.randomUUID?.() || String(Date.now()),
    };
    if (!online) {
      const q = loadQ(); q.push(body); saveQ(q);
      toast.success('📦 Offline queued · will auto-sync');
      setBusy(false); setSelected(null); setForm({ pickup_addr: '', drop_addr: '', distance_km: '', notes: '', provider_id: '' });
      return;
    }
    try {
      const r = await api.post('/citypro/orders', body);
      toast.success(`✓ Booked · ₹${r.data.order.amount_at} AT held`);
      setSelected(null); setForm({ pickup_addr: '', drop_addr: '', distance_km: '', notes: '', provider_id: '' });
      loadOrders();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Booking failed'); }
    finally { setBusy(false); }
  };

  const cancel = async (id) => {
    if (!window.confirm('Cancel this order? (AT will be refunded)')) return;
    try { await api.post(`/citypro/orders/${id}/cancel`); toast.success('Cancelled + refunded'); loadOrders(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  // group services by category
  const groups = useMemo(() => {
    const g = {};
    (cfg.services || []).forEach((s) => { (g[s.category] = g[s.category] || []).push(s); });
    return g;
  }, [cfg.services]);

  return (
    <div className="space-y-3">
      {!selected && (
        <div className="space-y-3" data-testid="cp-catalog">
          {Object.entries(groups).map(([cat, list]) => (
            <div key={cat} className="rounded-2xl border border-emerald-400/30 bg-slate-900/60 p-3">
              <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-black mb-2">{cat} · {list.length}</div>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {list.map((s) => (
                  <button key={s.id} onClick={() => setSelected(s)} data-testid={`cp-svc-${s.id}`}
                    className="rounded-xl border-2 border-slate-700 bg-slate-800/60 hover:bg-emerald-500/10 hover:border-emerald-400 p-3 flex flex-col items-center gap-1 transition text-center">
                    <div className="text-3xl">{s.icon}</div>
                    <div className="text-[10px] font-black leading-tight">{s.name}</div>
                    <div className="text-[9px] text-emerald-300 font-black">{s.base_at}+ AT</div>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {selected && (
        <div className="rounded-2xl border-2 border-emerald-400/50 bg-gradient-to-br from-emerald-900/40 to-slate-900 p-3" data-testid="cp-book-form">
          <div className="flex items-center gap-2 mb-3">
            <div className="text-3xl">{selected.icon}</div>
            <div className="flex-1">
              <div className="text-sm font-black">{selected.name}</div>
              <div className="text-[10px] opacity-70">{selected.category} · Base {selected.base_at} AT{selected.per_km ? ` + ${selected.per_km} AT/km` : ''}</div>
            </div>
            <button onClick={() => setSelected(null)} className="text-xs text-slate-400">✕</button>
          </div>
          {/* 🎤 Hold-to-Speak: fills pickup / drop / km / notes from Hinglish voice */}
          <div className="rounded-xl border border-fuchsia-400/40 bg-fuchsia-500/10 p-2 mb-2 flex flex-col sm:flex-row items-start sm:items-center gap-2">
            <div className="flex-1 text-[10px] opacity-90">
              <div className="font-black text-fuchsia-200">🎤 Voice Order — type mat karo, bol do!</div>
              <div className="opacity-70">Mic ko hold karke bolo: <i>"Pickup Ansal Plaza se, drop Sector 18, 5 km, notes fragile hai"</i></div>
            </div>
            <HoldToSpeak
              onTranscript={(text) => {
                const parsed = parseCityProVoice(text);
                setForm((f) => ({
                  ...f,
                  pickup_addr: parsed.pickup_addr || f.pickup_addr,
                  drop_addr: parsed.drop_addr || f.drop_addr,
                  distance_km: parsed.distance_km || f.distance_km,
                  notes: parsed.notes || f.notes,
                }));
                toast.success('✓ Form filled from voice — sun ke confirm karo');
                // Read back in Hindi so customer can verify without looking
                setTimeout(() => speakOrderReadbackHindi(parsed, selected), 350);
              }}
              disabled={busy}
            />
          </div>
          <div className="grid grid-cols-1 gap-2 mb-2">
            <input placeholder="Pickup address / details" value={form.pickup_addr} onChange={(e) => setForm({ ...form, pickup_addr: e.target.value })} data-testid="cp-pickup" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
            {selected.per_km > 0 && (
              <>
                <input placeholder="Drop address" value={form.drop_addr} onChange={(e) => setForm({ ...form, drop_addr: e.target.value })} data-testid="cp-drop" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
                <input type="number" step="0.1" placeholder="Estimated km" value={form.distance_km} onChange={(e) => setForm({ ...form, distance_km: e.target.value })} data-testid="cp-km" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
              </>
            )}
            <textarea rows={2} placeholder="Extra notes (optional)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="cp-notes" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          </div>
          {nearby && nearby.providers?.length > 0 && (
            <div className="mb-2" data-testid="cp-map-container">
              <div className="text-[10px] opacity-80 mb-1">🗺️ Live drivers nearby · ETA in minutes</div>
              <LiveMap providers={nearby.providers} center={nearby.center} radius_km={nearby.radius_km} />
              <div className="text-[9px] opacity-70 mt-1 text-center">Top driver: <b className="text-emerald-300">@{nearby.providers[0].username} · {nearby.providers[0].eta_min}m · {nearby.providers[0].distance_km}km{nearby.providers[0].rating_avg ? ` · ⭐${nearby.providers[0].rating_avg} (${nearby.providers[0].rating_count})` : ' · new'}</b></div>
            </div>
          )}
          {providers.length > 0 && (
            <div className="mb-2">
              <div className="text-[10px] opacity-80 mb-1">Pick a provider (optional):</div>
              <select value={form.provider_id} onChange={(e) => setForm({ ...form, provider_id: e.target.value })} data-testid="cp-provider" className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs">
                <option value="">— Any available —</option>
                {providers.map((p) => (
                  <option key={p.user_id} value={p.user_id}>@{p.username} {p.city ? `· ${p.city}` : ''}</option>
                ))}
              </select>
            </div>
          )}
          <div className="rounded-lg border border-emerald-400/30 bg-emerald-500/10 p-2 text-center mb-2">
            <div className="text-[10px] opacity-80">Estimated Fare</div>
            <div className="text-2xl font-black text-emerald-300">{estPrice} AT</div>
            <div className="text-[9px] opacity-70">33% CRR · 67% Provider · 7-Level Cascade</div>
          </div>
          <button onClick={book} disabled={busy} data-testid="cp-book-submit" className="w-full px-3 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 text-black text-sm font-black disabled:opacity-40 flex items-center justify-center gap-2">
            {busy ? '⏳' : online ? <><Send className="w-4 h-4" /> Book & Pay {estPrice} AT</> : <><Cloud className="w-4 h-4" /> Queue Offline</>}
          </button>
        </div>
      )}

      <div className="rounded-2xl border border-slate-700 bg-slate-900/60 p-3">
        <div className="text-[10px] uppercase tracking-widest text-slate-300 font-black mb-2">📦 My Orders · {orders.length}</div>
        <div className="max-h-72 overflow-y-auto space-y-1">
          {orders.map((o) => (
            <React.Fragment key={o.id}>
              <OrderRow order={o} isCustomer onCancel={() => cancel(o.id)} onRate={() => setRating({ order_id: o.id, provider: o.provider_username })} />
              {['accepted', 'in_progress'].includes(o.status) && <TrackOrderCard orderId={o.id} />}
            </React.Fragment>
          ))}
          {orders.length === 0 && <div className="text-xs opacity-60 text-center py-3">No orders yet</div>}
        </div>
        {loadQ().length > 0 && (
          <div className="mt-2 text-[10px] text-amber-300 text-center">📥 {loadQ().length} queued offline · will sync on reconnect</div>
        )}
      </div>
      {rating && <RatingModal orderId={rating.order_id} provider={rating.provider} onClose={() => setRating(null)} onDone={() => { setRating(null); loadOrders(); }} />}
    </div>
  );
}

function OrderRow({ order, isCustomer, isProvider, onAccept, onComplete, onCancel, onRate }) {
  const statusColor = {
    pending: 'text-amber-300 bg-amber-500/25',
    accepted: 'text-cyan-300 bg-cyan-500/25',
    in_progress: 'text-blue-300 bg-blue-500/25',
    completed: 'text-emerald-300 bg-emerald-500/25',
    cancelled: 'text-rose-300 bg-rose-500/25',
  }[order.status] || 'text-slate-300 bg-slate-500/25';
  return (
    <div className="rounded bg-slate-800/60 p-2 text-[11px]" data-testid={`cp-order-${order.id}`}>
      <div className="flex items-center gap-2">
        <b className="flex-1 truncate">{order.service_name}</b>
        <span className={`text-[9px] px-1.5 py-0.5 rounded font-black ${statusColor}`}>{order.status}</span>
        <span className="text-emerald-300 font-black">{order.amount_at} AT</span>
      </div>
      <div className="text-[10px] opacity-70 flex gap-2 flex-wrap">
        {order.pickup_addr && <span>📍 {order.pickup_addr.slice(0, 30)}</span>}
        {order.drop_addr && <span>→ {order.drop_addr.slice(0, 30)}</span>}
        {order.distance_km && <span>{order.distance_km}km</span>}
        <span>{fmtDateTime(order.created_at)}</span>
        {isCustomer && order.provider_username && <span>· 👤 @{order.provider_username}</span>}
        {isProvider && <span>· 🛒 @{order.customer_username}</span>}
        {order.rating && <span className="text-amber-300">· {'⭐'.repeat(order.rating)}</span>}
      </div>
      {order.notes && <div className="text-[10px] italic opacity-70 mt-0.5">📝 {order.notes}</div>}
      {isCustomer && order.status === 'accepted' && order.pickup_otp && (
        <div className="mt-1 rounded-lg border-2 border-amber-400 bg-gradient-to-r from-amber-500/20 to-yellow-500/20 p-2 text-center" data-testid={`cp-customer-otp-${order.id}`}>
          <div className="text-[9px] uppercase tracking-widest text-amber-200 font-black">🔐 Pickup OTP · Driver ko bataayein</div>
          <div className="text-2xl font-black tracking-[0.4em] text-amber-100 drop-shadow" style={{ fontFamily: 'monospace' }}>{order.pickup_otp}</div>
          <a
            href={`https://wa.me/?text=${encodeURIComponent(`🔐 *Anant CityPro · Pickup OTP*\nOrder ${order.id.slice(0,8)}\nOTP: *${order.pickup_otp}*\nDriver @${order.provider_username || ''}\nOnly share with your driver at pickup.`)}`}
            target="_blank" rel="noreferrer"
            data-testid={`cp-otp-notify-wa-${order.id}`}
            className="inline-block mt-1 px-2 py-0.5 rounded bg-emerald-500 text-black text-[9px] font-black">
            📱 Send OTP on WhatsApp
          </a>
        </div>
      )}
      {isProvider && order.status === 'accepted' && order.pickup_otp && (
        <OtpVerifyBox orderId={order.id} order={order} />
      )}
      {order.otp_verified_at && ['in_progress', 'completed'].includes(order.status) && (
        <div className="text-[9px] text-emerald-300 mt-0.5">✅ OTP verified · trip started</div>
      )}
      {['accepted', 'in_progress', 'completed'].includes(order.status) && <TripTimeline order={order} />}
      <div className="flex gap-1 mt-1">
        {isCustomer && ['pending', 'accepted'].includes(order.status) && (
          <button onClick={onCancel} data-testid={`cp-cancel-${order.id}`} className="px-2 py-0.5 rounded bg-red-500/30 text-red-300 text-[9px] font-black">✕ Cancel</button>
        )}
        {isCustomer && order.status === 'completed' && !order.rating && order.provider_username && (
          <button onClick={onRate} data-testid={`cp-rate-${order.id}`} className="px-2 py-0.5 rounded bg-gradient-to-r from-amber-500 to-yellow-400 text-black text-[9px] font-black">⭐ Rate Driver</button>
        )}
        {isProvider && order.status === 'pending' && (
          <button onClick={onAccept} data-testid={`cp-accept-${order.id}`} className="px-2 py-0.5 rounded bg-cyan-500 text-black text-[9px] font-black">✓ Accept</button>
        )}
        {isProvider && ['accepted', 'in_progress'].includes(order.status) && (
          <button onClick={onComplete} data-testid={`cp-complete-${order.id}`} className="px-2 py-0.5 rounded bg-emerald-500 text-black text-[9px] font-black">🏁 Complete</button>
        )}
        {isProvider && order.service_id === 'retail' && ['accepted', 'in_progress'].includes(order.status) && !order.delivery_partner_username && (
          <AssignDeliveryInline orderId={order.id} />
        )}
        {order.delivery_partner_username && (
          <span className="text-[9px] px-1.5 py-0.5 rounded bg-fuchsia-500/25 text-fuchsia-200 font-black">🛵 @{order.delivery_partner_username}</span>
        )}
        {order.status === 'completed' && order.cascade_summary && (
          <span className="text-[9px] opacity-70">💎 Cascade paid across {order.cascade_summary.levels || 0} levels</span>
        )}
      </div>
    </div>
  );
}

// ============ ASSIGN DELIVERY PARTNER (inline for merchant retail orders) ============
function AssignDeliveryInline({ orderId }) {
  const [uname, setUname] = useState('');
  const [busy, setBusy] = useState(false);
  const assign = async () => {
    if (!uname.trim()) { toast.error('Enter delivery partner username or ID'); return; }
    setBusy(true);
    try {
      // Try resolving as username first, else pass through as user_id
      let partnerId = uname.trim();
      try {
        const u = await api.get(`/wallets/lookup/${encodeURIComponent(partnerId)}`);
        if (u.data?.user?.id) partnerId = u.data.user.id;
      } catch { /* fall back to raw value */ }
      await api.post(`/citypro/orders/${orderId}/assign-delivery`, { delivery_partner_id: partnerId });
      toast.success('🛵 Delivery partner assigned');
      window.dispatchEvent(new CustomEvent('citypro-refresh'));
    } catch (e) { toast.error(e?.response?.data?.detail || 'Assign failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className="inline-flex items-center gap-1" data-testid={`cp-assign-delivery-${orderId}`}>
      <input value={uname} onChange={(e) => setUname(e.target.value)}
             placeholder="Delivery @username" className="px-1.5 py-0.5 rounded bg-slate-800 border border-slate-700 text-[9px] w-28" />
      <button onClick={assign} disabled={busy} data-testid={`cp-assign-delivery-btn-${orderId}`}
              className="px-2 py-0.5 rounded bg-fuchsia-500 text-white text-[9px] font-black disabled:opacity-50">
        {busy ? '⏳' : '🛵 Assign'}
      </button>
    </div>
  );
}

// ============ TRIP TIMELINE ============
function TripTimeline({ order }) {
  const steps = [
    { key: 'created_at',      label: 'Booked',       icon: '🛒', done: !!order.created_at },
    { key: 'accepted_at',     label: 'Accepted',     icon: '✅', done: !!order.accepted_at },
    { key: 'otp_verified_at', label: 'OTP Verified', icon: '🔐', done: !!order.otp_verified_at },
    { key: 'live_location',   label: 'In Progress',  icon: '🛰️', done: order.status === 'in_progress' || order.status === 'completed' },
    { key: 'completed_at',    label: 'Delivered',    icon: '🏁', done: order.status === 'completed' },
  ];
  return (
    <div className="mt-1 rounded-lg border border-slate-700 bg-slate-900/70 p-1.5" data-testid={`cp-timeline-${order.id}`}>
      <div className="flex items-center justify-between gap-1 overflow-x-auto">
        {steps.map((s, i) => (
          <React.Fragment key={s.key}>
            <div className={`flex flex-col items-center min-w-[48px] ${s.done ? 'opacity-100' : 'opacity-30'}`}>
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] ${s.done ? 'bg-emerald-500 text-black' : 'bg-slate-700 text-slate-400'}`}>{s.icon}</div>
              <div className={`text-[8px] font-black uppercase mt-0.5 ${s.done ? 'text-emerald-200' : 'text-slate-500'}`}>{s.label}</div>
            </div>
            {i < steps.length - 1 && <div className={`flex-1 h-0.5 ${s.done && steps[i+1].done ? 'bg-emerald-400' : 'bg-slate-700'}`} />}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

// ============ OTP VERIFY BOX (Provider side) — Big Keypad Modal ============
function OtpVerifyBox({ orderId, order }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid={`cp-otp-verify-${orderId}`}
              className="mt-1 w-full px-3 py-2 rounded-lg border-2 border-amber-400 bg-gradient-to-r from-amber-500/20 to-yellow-500/20 text-amber-100 text-xs font-black flex items-center justify-center gap-2">
        🔐 Enter Pickup OTP · Start Trip
      </button>
      {open && <OtpKeypadModal orderId={orderId} order={order} onClose={() => setOpen(false)} />}
    </>
  );
}

function OtpKeypadModal({ orderId, order, onClose }) {
  const [otp, setOtp] = useState('');
  const [busy, setBusy] = useState(false);
  const press = (n) => setOtp((v) => (v + n).slice(0, 4));
  const back = () => setOtp((v) => v.slice(0, -1));
  const submit = async () => {
    if (otp.length !== 4) { toast.error('4-digit OTP daalein'); return; }
    setBusy(true);
    try {
      const r = await api.post(`/citypro/orders/${orderId}/verify-otp`, { otp });
      toast.success('✅ OTP verified · trip started');
      // Fire family broadcast — open one WA link per emergency contact (best-effort)
      const fb = r.data?.family_broadcast;
      if (fb?.wa_links?.length) {
        fb.wa_links.slice(0, 3).forEach((l, i) => setTimeout(() => window.open(l.url, '_blank'), i * 400));
        toast.info(`👨‍👩‍👧 Family alerts sent to ${fb.wa_links.length} contacts`);
      }
      window.dispatchEvent(new CustomEvent('citypro-refresh'));
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'OTP mismatch'); }
    finally { setBusy(false); }
  };
  const keys = ['1','2','3','4','5','6','7','8','9','⌫','0','✓'];
  return (
    <div className="fixed inset-0 z-[70] bg-black/95 flex items-center justify-center p-4" onClick={onClose} data-testid={`cp-otp-keypad-${orderId}`}>
      <div className="bg-gradient-to-br from-slate-900 to-slate-950 border-2 border-amber-400 rounded-3xl p-5 max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
        <div className="text-center mb-3">
          <div className="text-[10px] uppercase tracking-widest text-amber-300 font-black">🔐 Pickup Verification</div>
          <div className="text-lg font-black text-amber-100 mt-1">@{order?.customer_username}</div>
          <div className="text-[10px] opacity-80 mt-0.5">📍 {order?.pickup_addr?.slice(0, 60) || '—'}</div>
        </div>
        <div className="flex items-center justify-center gap-2 mb-4">
          {[0,1,2,3].map((i) => (
            <div key={i} className={`w-12 h-14 rounded-xl border-2 flex items-center justify-center text-3xl font-black ${otp[i] ? 'border-amber-400 bg-amber-500/20 text-amber-100' : 'border-slate-700 bg-slate-800 text-slate-600'}`}>
              {otp[i] || '·'}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-3 gap-2">
          {keys.map((k) => (
            <button key={k} onClick={() => k === '⌫' ? back() : k === '✓' ? submit() : press(k)}
                    disabled={busy}
                    data-testid={`cp-keypad-${k}`}
                    className={`h-14 rounded-xl text-2xl font-black transition active:scale-95 ${
                      k === '✓' ? 'bg-gradient-to-r from-emerald-400 to-emerald-500 text-black' :
                      k === '⌫' ? 'bg-red-500/25 text-red-200 border border-red-400/40' :
                      'bg-slate-800 hover:bg-slate-700 text-white border border-slate-700'
                    }`}>
              {busy && k === '✓' ? '⏳' : k}
            </button>
          ))}
        </div>
        <button onClick={onClose} className="w-full mt-3 px-3 py-2 rounded bg-slate-800 text-slate-300 text-xs font-black">Cancel</button>
      </div>
    </div>
  );
}

// ============ RATING MODAL ============
function RatingModal({ orderId, provider, onClose, onDone }) {
  const [stars, setStars] = useState(5);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try { await api.post(`/citypro/orders/${orderId}/rate`, { stars, comment: comment || null }); toast.success(`✓ Rated ${stars}⭐`); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-sm flex items-center justify-center p-3" onClick={onClose}>
      <div className="bg-slate-900 border-2 border-amber-400 rounded-2xl p-4 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="cp-rate-modal">
        <div className="text-xs font-black text-amber-300 uppercase tracking-widest mb-2">⭐ Rate @{provider}</div>
        <div className="flex justify-center gap-1 mb-3">
          {[1, 2, 3, 4, 5].map((n) => (
            <button key={n} onClick={() => setStars(n)} data-testid={`cp-rate-${n}`} className={`text-4xl transition ${stars >= n ? 'opacity-100 scale-110' : 'opacity-30'}`}>⭐</button>
          ))}
        </div>
        <textarea rows={3} placeholder="Optional feedback…" value={comment} onChange={(e) => setComment(e.target.value)} data-testid="cp-rate-cmt" className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs mb-3" />
        <div className="flex gap-2">
          <button onClick={onClose} className="px-3 py-2 rounded bg-slate-700 text-slate-200 text-xs font-black">Skip</button>
          <button onClick={submit} disabled={busy} data-testid="cp-rate-submit" className="flex-1 px-3 py-2 rounded bg-gradient-to-r from-amber-500 to-yellow-400 text-black text-sm font-black">{busy ? '⏳' : '✓ Submit Rating'}</button>
        </div>
      </div>
    </div>
  );
}

// ============ PROVIDER — accept + complete orders + live GPS push ============
function ProviderTab({ cfg }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/citypro/orders', { params: { role: 'provider', limit: 50 } }); setOrders(r.data?.orders || []); }
    catch { /* silent */ }
    finally { setLoading(false); }
  }, []);
  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    const h = () => load();
    window.addEventListener('citypro-refresh', h);
    return () => { clearInterval(t); window.removeEventListener('citypro-refresh', h); };
  }, [load]);

  // Live GPS push for accepted / in_progress orders
  useEffect(() => {
    const active = orders.filter((o) => ['accepted', 'in_progress'].includes(o.status));
    if (active.length === 0 || !navigator.geolocation) return;
    const push = () => {
      navigator.geolocation.getCurrentPosition((p) => {
        const body = { latitude: p.coords.latitude, longitude: p.coords.longitude,
                       heading: p.coords.heading || null, speed_kmh: p.coords.speed ? p.coords.speed * 3.6 : null };
        active.forEach((o) => api.post(`/citypro/orders/${o.id}/location`, body).catch(() => {}));
      }, () => {}, { enableHighAccuracy: true, maximumAge: 5000, timeout: 8000 });
    };
    push();
    const t = setInterval(push, 15000);
    return () => clearInterval(t);
  }, [orders]);

  const accept = async (id) => {
    try { await api.post(`/citypro/orders/${id}/accept`); toast.success('Accepted!'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const complete = async (id) => {
    if (!window.confirm('Mark as completed? (AT will be split: 67% to you + 33% cascade)')) return;
    try {
      const r = await api.post(`/citypro/orders/${id}/complete`);
      toast.success(`✓ +${r.data.provider_share_at} AT credited`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const roles = (cfg.profile?.roles || []).filter((r) => r !== 'customer');
  const activeCount = orders.filter((o) => ['accepted', 'in_progress'].includes(o.status)).length;
  if (roles.length === 0) return (
    <div className="rounded-2xl border-2 border-amber-400/40 bg-amber-500/10 p-4 text-center text-xs" data-testid="cp-no-provider">
      Aap abhi provider nahi hain. <b>Profile tab</b> me jaake driver / laundry / merchant role add karein.
    </div>
  );

  return (
    <div className="space-y-3" data-testid="cp-provider-tab">
      <div className="rounded-2xl border border-cyan-400/30 bg-cyan-500/10 p-3">
        <div className="flex items-center justify-between mb-1">
          <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-black">🎯 Aapke Roles</div>
          {activeCount > 0 && <span className="text-[9px] px-2 py-0.5 rounded-full bg-emerald-500 text-black font-black animate-pulse">🛰️ GPS live ({activeCount})</span>}
        </div>
        <div className="flex flex-wrap gap-1">
          {roles.map((r) => <span key={r} className="text-[10px] px-2 py-0.5 rounded-full bg-cyan-500/25 border border-cyan-400/40 font-black">{r}</span>)}
        </div>
      </div>
      <div className="rounded-2xl border border-slate-700 bg-slate-900/60 p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-black">📋 Available + My Orders · {orders.length}</div>
          <button onClick={load} className="text-[9px] px-2 py-0.5 rounded bg-slate-800 text-slate-300">{loading ? '⏳' : '↻'} Refresh</button>
        </div>
        <div className="max-h-96 overflow-y-auto space-y-1">
          {orders.map((o) => (
            <OrderRow key={o.id} order={o} isProvider onAccept={() => accept(o.id)} onComplete={() => complete(o.id)} />
          ))}
          {orders.length === 0 && <div className="text-xs opacity-60 text-center py-3">No open or assigned orders</div>}
        </div>
      </div>
    </div>
  );
}

// ============ CASH CENTER — AT ↔ Cash exchange (3% fee) ============
function CashCenterTab({ cfg }) {
  const [centers, setCenters] = useState([]);
  const [selected, setSelected] = useState('');
  const [amount, setAmount] = useState('');
  const [direction, setDirection] = useState('at_to_cash');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get('/citypro/providers', { params: { role: 'cash_center', limit: 30 } })
      .then((r) => setCenters(r.data?.providers || [])).catch(() => {});
  }, []);
  const fee = Math.round(parseFloat(amount || 0) * 0.03 * 100) / 100;
  const net = Math.round((parseFloat(amount || 0) - fee) * 100) / 100;

  const submit = async () => {
    if (!selected) { toast.error('Select cash center'); return; }
    if (!(parseFloat(amount) > 0)) { toast.error('Enter amount'); return; }
    setBusy(true);
    try {
      const r = await api.post('/citypro/cash-exchange', { center_user_id: selected, at_amount: parseFloat(amount), direction });
      toast.success(`✓ Done · Fee ${r.data.fee_at} · Net ${r.data.net_at} AT`);
      setAmount('');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="cp-cash-tab">
      <div className="rounded-2xl border-2 border-amber-400/50 bg-gradient-to-br from-amber-900/30 to-slate-900 p-3">
        <div className="text-[10px] uppercase tracking-widest text-amber-300 font-black mb-2 flex items-center gap-1"><ArrowUpDown className="w-3 h-3" /> AT ↔ Cash Exchange · 3% fee (No Burn · flows to cascade)</div>
        <div className="grid grid-cols-2 gap-2 mb-2">
          <button onClick={() => setDirection('at_to_cash')} data-testid="cp-dir-atc" className={`px-3 py-2 rounded text-xs font-black ${direction === 'at_to_cash' ? 'bg-amber-500 text-black' : 'bg-slate-800 text-amber-200'}`}>AT → Cash (withdraw)</button>
          <button onClick={() => setDirection('cash_to_at')} data-testid="cp-dir-cta" className={`px-3 py-2 rounded text-xs font-black ${direction === 'cash_to_at' ? 'bg-emerald-500 text-black' : 'bg-slate-800 text-emerald-200'}`}>Cash → AT (top-up)</button>
        </div>
        <select value={selected} onChange={(e) => setSelected(e.target.value)} data-testid="cp-cash-center" className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs mb-2">
          <option value="">— Select Cash Center —</option>
          {centers.map((c) => <option key={c.user_id} value={c.user_id}>@{c.username} {c.city ? `· ${c.city}` : ''} {c.phone ? `· 📞 ${c.phone}` : ''}</option>)}
        </select>
        <input type="number" placeholder="AT Amount" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="cp-cash-amt" className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs mb-2" />
        {amount && (
          <div className="rounded bg-slate-800/60 p-2 text-[11px] mb-2">
            <div className="flex justify-between"><span>Gross</span><b>{amount} AT</b></div>
            <div className="flex justify-between text-amber-300"><span>Fee 3%</span><b>−{fee}</b></div>
            <div className="flex justify-between border-t border-slate-700 pt-1 mt-1"><span>Net</span><b className="text-emerald-300">{net} AT</b></div>
          </div>
        )}
        <button onClick={submit} disabled={busy} data-testid="cp-cash-submit" className="w-full px-3 py-2 rounded bg-gradient-to-r from-amber-500 to-yellow-400 text-black text-sm font-black disabled:opacity-40">{busy ? '⏳' : '💸 Confirm Exchange'}</button>
      </div>
      <div className="rounded-2xl border border-slate-700 bg-slate-900/60 p-3">
        <div className="text-[10px] uppercase tracking-widest text-slate-300 font-black mb-2">🏪 {centers.length} Cash Centers Nearby</div>
        <div className="text-[10px] opacity-70">Cash Centers earn on every exchange · Register your shop as Cash Center in the Profile tab.</div>
      </div>
    </div>
  );
}

// ============ PROFILE — multi-role register + online toggle ============
const ALL_ROLES = [
  ['customer', '🛒 Customer'],
  ['driver_taxi', '🚕 Taxi Driver'], ['driver_auto', '🛺 Auto Driver'], ['driver_bike', '🏍️ Bike Taxi'],
  ['delivery_partner', '🛵 Delivery Partner'],
  ['laundry', '🧺 Laundry'], ['doctor', '🏥 Doctor (Tele)'],
  ['merchant', '🛍️ Merchant'], ['flour_mill', '🌾 Flour Mill'], ['tailor', '✂️ Tailor'],
  ['technician', '🔧 Technician'], ['grocery', '🛒 Grocery'], ['dairy', '🥛 Dairy'],
  ['water', '💧 Water'], ['salon', '💇 Salon'], ['tutor', '📚 Tutor'],
  ['cash_center', '💸 Cash Center'],
];

function ProfileTab({ cfg, onReload }) {
  const p = cfg.profile || {};
  const cats = cfg.merchant_categories || [];
  const plans = cfg.shop_plans || [];
  const [form, setForm] = useState({
    city: p.city || '', phone: p.phone || '', whatsapp_number: p.whatsapp_number || '',
    roles: p.roles || ['customer'],
    vehicle_no: p.vehicle_no || '', shop_name: p.shop_name || '',
    active: p.active !== undefined ? p.active : true,
    latitude: p.latitude || null, longitude: p.longitude || null,
    merchant_category: p.merchant_category || '',
    merchant_subcategories: p.merchant_subcategories || [],
    banner_url: p.banner_url || '',
    front_image_url: p.front_image_url || '',
    special_offer: p.special_offer || '',
    plan_tier: p.plan_tier || '',
    delivery_charge_at: p.delivery_charge_at || '',
    emergency_contacts: p.emergency_contacts || [],
    speed_tier: p.speed_tier || 'standard',
    availability: p.availability || 'available',
  });
  const [busy, setBusy] = useState(false);
  const [kycOpen, setKycOpen] = useState(false);
  const [kycList, setKycList] = useState([]);
  const toggleRole = (r) => setForm((f) => ({ ...f, roles: f.roles.includes(r) ? f.roles.filter((x) => x !== r) : [...f.roles, r] }));
  const toggleSub = (s) => setForm((f) => ({ ...f, merchant_subcategories: f.merchant_subcategories.includes(s) ? f.merchant_subcategories.filter((x) => x !== s) : [...f.merchant_subcategories, s] }));
  const catObj = cats.find((c) => c.id === form.merchant_category);
  const isMerchant = form.roles.includes('merchant');
  const isDelivery = form.roles.some((r) => r.startsWith('delivery') || r.startsWith('driver_'));
  const isCash = form.roles.includes('cash_center');
  useEffect(() => { api.get('/citypro/kyc/me').then((r) => setKycList(r.data?.kyc || [])).catch(() => {}); }, [kycOpen]);
  const gps = () => {
    if (!navigator.geolocation) { toast.error('GPS not available'); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => { setForm((f) => ({ ...f, latitude: pos.coords.latitude, longitude: pos.coords.longitude })); toast.success('📍 Location saved'); },
      () => toast.error('GPS denied'),
    );
  };
  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...form, delivery_charge_at: form.delivery_charge_at ? parseFloat(form.delivery_charge_at) : null };
      await api.post('/citypro/profile', payload); toast.success('✓ Profile saved'); onReload();
    }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  const addContact = () => setForm((f) => ({ ...f, emergency_contacts: [...f.emergency_contacts, { name: '', phone: '' }] }));
  const setContact = (i, k, v) => setForm((f) => ({ ...f, emergency_contacts: f.emergency_contacts.map((c, ii) => ii === i ? { ...c, [k]: v } : c) }));
  const removeContact = (i) => setForm((f) => ({ ...f, emergency_contacts: f.emergency_contacts.filter((_, ii) => ii !== i) }));
  const subscribePlan = async (planId) => {
    if (!window.confirm(`Subscribe to ${planId.toUpperCase()} plan? AT will be debited monthly.`)) return;
    try { const r = await api.post('/citypro/plans/subscribe', { plan_id: planId }); toast.success(`✓ ${r.data.plan.name} activated`); onReload(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-3" data-testid="cp-profile-tab">
      <div className="rounded-2xl border-2 border-cyan-400/50 bg-gradient-to-br from-cyan-900/30 to-slate-900 p-3">
        <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-black mb-2">⚙️ Multi-Role Profile</div>
        <div className="grid grid-cols-2 gap-2 mb-2">
          <input placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="cp-city" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          <input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="cp-phone" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          <input placeholder="WhatsApp number" value={form.whatsapp_number} onChange={(e) => setForm({ ...form, whatsapp_number: e.target.value })} data-testid="cp-wa" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          <input placeholder="Vehicle No. (drivers)" value={form.vehicle_no} onChange={(e) => setForm({ ...form, vehicle_no: e.target.value })} className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          <input placeholder="Shop / Business Name" value={form.shop_name} onChange={(e) => setForm({ ...form, shop_name: e.target.value })} className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          <input placeholder="Delivery Charge (AT/order)" type="number" value={form.delivery_charge_at} onChange={(e) => setForm({ ...form, delivery_charge_at: e.target.value })} data-testid="cp-delivery-charge" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
        </div>
        {/* Speed tier + availability (delivery partners) */}
        {isDelivery && (
          <div className="grid grid-cols-2 gap-2 mb-2 rounded-xl border border-emerald-400/40 bg-emerald-500/10 p-2">
            <label className="flex flex-col text-[10px]">
              <span className="opacity-80 mb-0.5">🚀 Speed Tier</span>
              <select value={form.speed_tier} onChange={(e) => setForm({ ...form, speed_tier: e.target.value })} data-testid="cp-speed-tier" className="px-2 py-1 rounded bg-slate-800 border border-slate-700 text-xs">
                <option value="standard">Standard</option>
                <option value="fast">⚡ Fast</option>
                <option value="super_fast">🚀 Super Fast</option>
              </select>
            </label>
            <label className="flex flex-col text-[10px]">
              <span className="opacity-80 mb-0.5">🟢 Availability</span>
              <select value={form.availability} onChange={(e) => setForm({ ...form, availability: e.target.value })} data-testid="cp-availability" className="px-2 py-1 rounded bg-slate-800 border border-slate-700 text-xs">
                <option value="available">🟢 Available</option>
                <option value="ready">✅ Ready to Deliver</option>
                <option value="not_available">🔴 Not Available</option>
              </select>
            </label>
          </div>
        )}
        <button onClick={gps} data-testid="cp-gps" className="w-full px-3 py-1.5 rounded bg-slate-800 text-cyan-200 text-xs font-black mb-2 flex items-center justify-center gap-1"><MapPin className="w-3 h-3" /> {form.latitude ? `📍 ${form.latitude.toFixed(3)}, ${form.longitude.toFixed(3)}` : 'Save Current Location'}</button>
        <label className="flex items-center gap-2 text-xs mb-2">
          <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="cp-active" />
          <span>🟢 Available Online (accept incoming orders)</span>
        </label>
        <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-black mb-1">Roles you play:</div>
        <div className="grid grid-cols-2 gap-1 mb-2 max-h-52 overflow-y-auto">
          {ALL_ROLES.map(([id, l]) => (
            <label key={id} className={`flex items-center gap-1 text-[11px] px-2 py-1 rounded cursor-pointer transition ${form.roles.includes(id) ? 'bg-emerald-500/25 border border-emerald-400/40' : 'bg-slate-800/60 border border-slate-700'}`}>
              <input type="checkbox" checked={form.roles.includes(id)} onChange={() => toggleRole(id)} data-testid={`cp-role-${id}`} />
              <span>{l}</span>
            </label>
          ))}
        </div>
        {isMerchant && (
          <div className="rounded-xl border-2 border-amber-400/40 bg-amber-500/10 p-2 mb-2" data-testid="cp-merchant-category-section">
            <div className="text-[10px] uppercase tracking-widest text-amber-300 font-black mb-1">🏬 Merchant Category (required)</div>
            <select value={form.merchant_category} onChange={(e) => setForm({ ...form, merchant_category: e.target.value, merchant_subcategories: [] })} data-testid="cp-mcat" className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs mb-2">
              <option value="">— Select category —</option>
              {cats.map((c) => <option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
            </select>
            {catObj && (
              <>
                <div className="text-[10px] opacity-80 mb-1">Sub-categories (multi-select):</div>
                <div className="flex flex-wrap gap-1">
                  {(catObj.sub || []).map((s) => (
                    <button key={s} onClick={() => toggleSub(s)} data-testid={`cp-msub-${s}`} className={`text-[10px] px-2 py-0.5 rounded font-black ${form.merchant_subcategories.includes(s) ? 'bg-amber-500 text-black' : 'bg-slate-800 text-slate-300'}`}>{s}</button>
                  ))}
                </div>
              </>
            )}
            <input placeholder="Shop Front / Display Image URL" value={form.front_image_url} onChange={(e) => setForm({ ...form, front_image_url: e.target.value })} data-testid="cp-front-img" className="w-full mt-2 px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
            <input placeholder="Banner image URL (optional)" value={form.banner_url} onChange={(e) => setForm({ ...form, banner_url: e.target.value })} className="w-full mt-1 px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
            <input placeholder="Special Offer / Sale banner text" value={form.special_offer} onChange={(e) => setForm({ ...form, special_offer: e.target.value })} data-testid="cp-special-offer" className="w-full mt-1 px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          </div>
        )}
        {/* Emergency Contacts */}
        <div className="rounded-xl border border-rose-400/40 bg-rose-500/10 p-2 mb-2" data-testid="cp-emergency-section">
          <div className="text-[10px] uppercase tracking-widest text-rose-300 font-black mb-1 flex items-center justify-between">
            <span>🚨 Emergency Family Contacts</span>
            <button onClick={addContact} data-testid="cp-add-contact" className="text-[9px] px-2 py-0.5 rounded bg-rose-500 text-white font-black">+ Add</button>
          </div>
          <div className="text-[9px] opacity-80 mb-1">On OTP verify, safety alert auto-broadcasts to these contacts on WhatsApp</div>
          {form.emergency_contacts.map((c, i) => (
            <div key={i} className="flex gap-1 mb-1">
              <input placeholder="Name" value={c.name} onChange={(e) => setContact(i, 'name', e.target.value)} data-testid={`cp-ec-name-${i}`} className="flex-1 px-1.5 py-1 rounded bg-slate-800 border border-slate-700 text-[11px]" />
              <input placeholder="Phone (with country code)" value={c.phone} onChange={(e) => setContact(i, 'phone', e.target.value)} data-testid={`cp-ec-phone-${i}`} className="flex-1 px-1.5 py-1 rounded bg-slate-800 border border-slate-700 text-[11px]" />
              <button onClick={() => removeContact(i)} className="px-1.5 py-1 rounded bg-red-500/30 text-red-200 text-[9px]">✕</button>
            </div>
          ))}
          {form.emergency_contacts.length === 0 && <div className="text-[10px] opacity-60 text-center py-1">No contacts added</div>}
        </div>
        <button onClick={save} disabled={busy} data-testid="cp-profile-save" className="w-full px-3 py-2 rounded bg-gradient-to-r from-emerald-500 to-cyan-500 text-black text-sm font-black disabled:opacity-40">{busy ? '⏳' : '💾 Save Profile'}</button>
      </div>

      {/* Shop Listing Plans */}
      {isMerchant && (
        <div className="rounded-2xl border-2 border-fuchsia-400/40 bg-gradient-to-br from-fuchsia-950/30 to-slate-900 p-3" data-testid="cp-shop-plans">
          <div className="text-[10px] uppercase tracking-widest text-fuchsia-300 font-black mb-2">💎 Shop Listing Plans · Current: <span className="text-amber-300">{form.plan_tier || 'None'}</span></div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {plans.map((pl) => (
              <div key={pl.id} className={`rounded-xl border-2 p-2 ${form.plan_tier === pl.id ? 'border-fuchsia-400 bg-fuchsia-500/10' : 'border-slate-700 bg-slate-800/40'}`}>
                <div className="text-sm font-black">{pl.name}</div>
                <div className="text-lg font-black text-fuchsia-300">{pl.at_month} AT<span className="text-[10px] opacity-70">/mo</span></div>
                <ul className="text-[10px] opacity-80 mt-1 space-y-0.5 list-disc pl-4">{pl.features.map((f) => <li key={f}>{f}</li>)}</ul>
                <button onClick={() => subscribePlan(pl.id)} data-testid={`cp-plan-sub-${pl.id}`} className="w-full mt-2 px-2 py-1 rounded bg-fuchsia-500 text-white text-[10px] font-black">Subscribe</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Merchant Ads Wallet — pay AT to boost showcase position */}
      {isMerchant && <MerchantBoostCard onReload={onReload} />}

      {/* KYC & Approval */}
      {(isDelivery || isMerchant || isCash) && (
        <div className="rounded-2xl border-2 border-amber-400/40 bg-gradient-to-br from-amber-950/30 to-slate-900 p-3" data-testid="cp-kyc-section">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase tracking-widest text-amber-300 font-black">🆔 KYC · Approval Request</div>
            <button onClick={() => setKycOpen(true)} data-testid="cp-kyc-open" className="px-2 py-0.5 rounded bg-amber-500 text-black text-[10px] font-black">Submit / Update KYC</button>
          </div>
          <div className="text-[10px] opacity-80 mb-1">Delivery Partner · Merchant · Cash Counter — KYC verify hone par admin approval milegi</div>
          {kycList.length === 0 && <div className="text-[10px] opacity-60">No KYC submitted yet</div>}
          {kycList.map((k) => (
            <div key={k.id} className="flex items-center justify-between text-[10px] bg-slate-800/60 rounded p-1.5 mt-1">
              <span><b>{k.role}</b> · {k.doc_type} · {k.doc_number}</span>
              <span className={`px-2 py-0.5 rounded font-black ${k.status === 'approved' ? 'bg-emerald-500 text-black' : k.status === 'rejected' ? 'bg-red-500 text-white' : 'bg-amber-500 text-black'}`}>{k.status}</span>
            </div>
          ))}
        </div>
      )}
      {kycOpen && <KycModal onClose={() => setKycOpen(false)} roleDefault={isDelivery ? 'delivery_partner' : isCash ? 'cash_center' : 'merchant'} />}
    </div>
  );
}

// ============ KYC SUBMIT MODAL ============
function KycModal({ onClose, roleDefault }) {
  const [form, setForm] = useState({ role: roleDefault, full_name: '', doc_type: 'aadhaar', doc_number: '', doc_image_url: '', selfie_url: '', address: '', notes: '' });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!form.full_name || !form.doc_number) { toast.error('Full name + doc number required'); return; }
    setBusy(true);
    try { await api.post('/citypro/kyc/submit', form); toast.success('✓ KYC submitted for review'); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] bg-black/85 flex items-center justify-center p-3 overflow-y-auto" onClick={onClose} data-testid="cp-kyc-modal">
      <div className="bg-slate-900 border-2 border-amber-400 rounded-2xl p-4 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="text-xs font-black text-amber-300 uppercase tracking-widest mb-3">🆔 KYC Submission</div>
        <div className="grid grid-cols-2 gap-2">
          <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="cp-kyc-role" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs col-span-2">
            <option value="delivery_partner">🛵 Delivery Partner</option>
            <option value="merchant">🏬 Merchant</option>
            <option value="cash_center">💸 Cash Counter</option>
          </select>
          <input placeholder="Full Legal Name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="cp-kyc-name" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs col-span-2" />
          <select value={form.doc_type} onChange={(e) => setForm({ ...form, doc_type: e.target.value })} className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs">
            <option value="aadhaar">Aadhaar</option><option value="pan">PAN</option><option value="dl">Driving Licence</option><option value="gst">GST</option>
          </select>
          <input placeholder="Document Number" value={form.doc_number} onChange={(e) => setForm({ ...form, doc_number: e.target.value })} data-testid="cp-kyc-num" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          <input placeholder="Doc Image URL" value={form.doc_image_url} onChange={(e) => setForm({ ...form, doc_image_url: e.target.value })} className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs col-span-2" />
          <input placeholder="Selfie URL (optional)" value={form.selfie_url} onChange={(e) => setForm({ ...form, selfie_url: e.target.value })} className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs col-span-2" />
          <textarea rows={2} placeholder="Address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs col-span-2" />
        </div>
        <button onClick={submit} disabled={busy} data-testid="cp-kyc-submit" className="w-full mt-3 px-3 py-2 rounded-xl bg-amber-500 text-black text-sm font-black disabled:opacity-40">
          {busy ? '⏳' : '📤 Submit for Approval'}
        </button>
      </div>
    </div>
  );
}

// ============ TRACK ORDER — live driver pin polling ============
function TrackOrderCard({ orderId }) {
  const [live, setLive] = useState(null);
  const [status, setStatus] = useState(null);
  const [myLoc, setMyLoc] = useState(null);
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((p) => setMyLoc({ lat: p.coords.latitude, lng: p.coords.longitude }));
    }
    let mounted = true;
    const poll = async () => {
      try {
        const r = await api.get(`/citypro/orders/${orderId}/location`);
        if (!mounted) return;
        setLive(r.data?.live_location); setStatus(r.data?.status);
      } catch { /* silent */ }
    };
    poll();
    const t = setInterval(poll, 10000);
    return () => { mounted = false; clearInterval(t); };
  }, [orderId]);
  if (!live && !myLoc) return null;
  // Calculate distance if both available
  let dist = null;
  if (live && myLoc) {
    const R = 6371;
    const toRad = (v) => v * Math.PI / 180;
    const dp = toRad(live.latitude - myLoc.lat);
    const dl = toRad(live.longitude - myLoc.lng);
    const a = Math.sin(dp/2)**2 + Math.cos(toRad(myLoc.lat)) * Math.cos(toRad(live.latitude)) * Math.sin(dl/2)**2;
    dist = 2 * R * Math.asin(Math.sqrt(a));
  }
  return (
    <div className="rounded-xl border-2 border-emerald-400/60 bg-gradient-to-br from-emerald-900/40 to-slate-900 p-2 mt-1" data-testid={`cp-track-${orderId}`}>
      <div className="flex items-center justify-between mb-1">
        <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-black">🛰️ Live Tracking · {status}</div>
        {dist !== null && <span className="text-[10px] font-black text-emerald-200">{dist.toFixed(2)}km · ~{Math.max(1, Math.round(dist * 2.5))}m</span>}
      </div>
      {live && myLoc && (
        <LiveMap
          providers={[{ user_id: 'driver', username: 'driver', latitude: live.latitude, longitude: live.longitude, eta_min: Math.max(1, Math.round((dist || 0) * 2.5)), rating_avg: 0 }]}
          center={{ lat: myLoc.lat, lng: myLoc.lng }} radius_km={Math.max(2, dist || 2)}
        />
      )}
      {!live && <div className="text-[10px] opacity-70 text-center py-3">Waiting for driver GPS ping…</div>}
      {live?.speed_kmh && <div className="text-[9px] opacity-70 mt-1">🚗 {live.speed_kmh.toFixed(0)} km/h · updated {new Date(live.ts).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</div>}
    </div>
  );
}

// ============ LIVE MAP (CSS/SVG mock — no external map lib) ============
export function LiveMap({ providers, center, radius_km }) {
  // Normalize positions into a 300x160 canvas
  const w = 320, h = 200, cx = w / 2, cy = h / 2;
  const km2px = 60 / Math.max(1, radius_km || 5); // 1km ≈ 12px on 5km radius
  return (
    <div className="relative rounded-xl border-2 border-emerald-400/40 bg-gradient-to-br from-slate-900 to-slate-950 overflow-hidden" style={{ height: h, width: '100%' }} data-testid="cp-live-map">
      {/* Grid */}
      <svg viewBox={`0 0 ${w} ${h}`} className="absolute inset-0 w-full h-full">
        <defs>
          <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
            <path d="M 20 0 L 0 0 0 20" fill="none" stroke="rgba(16,185,129,0.15)" strokeWidth="0.5" />
          </pattern>
          <radialGradient id="pulse" cx="50%" cy="50%">
            <stop offset="0%" stopColor="rgba(16,185,129,0.4)" />
            <stop offset="100%" stopColor="rgba(16,185,129,0)" />
          </radialGradient>
        </defs>
        <rect width={w} height={h} fill="url(#grid)" />
        {/* Radius rings */}
        {[0.33, 0.66, 1].map((f, i) => (
          <circle key={i} cx={cx} cy={cy} r={f * (Math.min(w, h) / 2 - 8)} fill="none" stroke="rgba(16,185,129,0.25)" strokeDasharray="3 3" />
        ))}
        <circle cx={cx} cy={cy} r={40} fill="url(#pulse)">
          <animate attributeName="r" values="30;60;30" dur="2s" repeatCount="indefinite" />
        </circle>
        {/* Center pin (you) */}
        <g>
          <circle cx={cx} cy={cy} r={7} fill="#0891b2" stroke="#fff" strokeWidth="2" />
          <text x={cx} y={cy - 12} fill="#67e8f9" fontSize="10" fontWeight="bold" textAnchor="middle">YOU</text>
        </g>
        {/* Providers */}
        {providers.slice(0, 12).map((p, i) => {
          const dx = (p.longitude - center.lng) * 111 * Math.cos(center.lat * Math.PI / 180);
          const dy = (center.lat - p.latitude) * 111;
          const px = cx + dx * km2px;
          const py = cy + dy * km2px;
          if (px < 5 || px > w - 5 || py < 15 || py > h - 5) return null;
          return (
            <g key={p.user_id || i}>
              <circle cx={px} cy={py} r={(p.rating_avg || 0) >= 4.5 ? 5 : 4} fill={(p.rating_avg || 0) >= 4.5 ? '#fbbf24' : '#10b981'} stroke="#fff" strokeWidth="1">
                <animate attributeName="opacity" values="1;0.4;1" dur="1.8s" repeatCount="indefinite" />
              </circle>
              <text x={px} y={py - 8} fill="#a7f3d0" fontSize="8" textAnchor="middle" fontWeight="bold">{p.eta_min}m{p.rating_avg ? ` ⭐${p.rating_avg}` : ''}</text>
            </g>
          );
        })}
      </svg>
      <div className="absolute bottom-1 left-2 text-[9px] text-emerald-300 opacity-80 font-black tracking-widest">🟢 {providers.length} LIVE · {radius_km}km radius</div>
    </div>
  );
}

// ============ SHOPS TAB — merchant directory + product browse ============
function ShopsTab({ cfg }) {
  const cats = cfg.merchant_categories || [];
  const [cat, setCat] = useState('');
  const [sub, setSub] = useState('');
  const [merchants, setMerchants] = useState([]);
  const [openMerchant, setOpenMerchant] = useState(null);
  const [products, setProducts] = useState([]);
  const [manageProducts, setManageProducts] = useState(false);
  const [promos, setPromos] = useState([]);
  const [cart, setCart] = useState(() => { try { return JSON.parse(localStorage.getItem('cp_cart') || '[]'); } catch { return []; } });
  const [showCart, setShowCart] = useState(false);
  const [customReq, setCustomReq] = useState(false);
  const saveCart = (c) => { setCart(c); localStorage.setItem('cp_cart', JSON.stringify(c)); };
  const addToCart = (m, p) => {
    const cur = cart.filter((x) => x.merchant_id === m.user_id || cart.length === 0 ? true : false);
    const existing = cart.find((x) => x.id === p.id);
    if (existing) { saveCart(cart.map((x) => x.id === p.id ? { ...x, qty: x.qty + 1 } : x)); }
    else { saveCart([...cart, { id: p.id, name: p.name, price_at: p.price_at, image_url: p.image_url, merchant_id: m.user_id, merchant_name: m.shop_name || m.username, qty: 1 }]); }
    toast.success(`✓ ${p.name} added to cart`);
    void cur;
  };
  const shareProduct = (m, p) => {
    const url = `${window.location.origin}/citypro`;
    // WhatsApp respects the last URL in text for preview — put image_url last so WA renders the image card
    const parts = [
      `🛍️ *${p.name}*`,
      `💰 ${p.price_at} AT`,
      p.description ? p.description : '',
      `Shop: ${m.shop_name || '@' + m.username}`,
      url,
      p.image_url ? p.image_url : '',
    ].filter(Boolean);
    const text = parts.join('\n');
    // Try native share with image as file first (mobile) — WhatsApp picks it up as photo attachment
    (async () => {
      try {
        if (p.image_url && navigator.canShare && navigator.share) {
          const resp = await fetch(p.image_url);
          if (resp.ok) {
            const blob = await resp.blob();
            const file = new File([blob], `${p.name}.jpg`, { type: blob.type || 'image/jpeg' });
            if (navigator.canShare({ files: [file] })) {
              await navigator.share({ files: [file], title: p.name, text });
              return;
            }
          }
        }
        if (navigator.share) { await navigator.share({ title: p.name, text, url }); return; }
      } catch { /* fall through to WA link */ }
      // Fallback: open WhatsApp with pre-filled text (WA renders image preview from last URL)
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
    })();
  };

  const load = useCallback(async () => {
    if (!cat) { setMerchants([]); setPromos([]); return; }
    try {
      const [m, p] = await Promise.all([
        api.get('/citypro/merchants', { params: { category: cat, subcategory: sub || undefined } }),
        api.get('/citypro/promos', { params: { category: cat } }),
      ]);
      setMerchants(m.data?.merchants || []);
      setPromos(p.data?.promos || []);
    } catch { /* silent */ }
  }, [cat, sub]);
  useEffect(() => { load(); }, [load]);

  const loadProducts = async (mid) => {
    try { const r = await api.get('/citypro/products', { params: { merchant_id: mid, limit: 60 } }); setProducts(r.data?.products || []); }
    catch { setProducts([]); }
  };

  const openShop = async (m) => { setOpenMerchant(m); await loadProducts(m.user_id); };
  const catObj = cats.find((c) => c.id === cat);
  const isMyShop = openMerchant && openMerchant.user_id === (cfg.user?.id);

  return (
    <div className="space-y-3" data-testid="cp-shops-tab">
      <div className="flex flex-wrap items-center gap-2">
        <Link to="/citypro/showcase" data-testid="cp-goto-showcase" className="flex-1 px-3 py-2 rounded-xl bg-gradient-to-r from-fuchsia-500 to-pink-500 text-white text-xs font-black text-center">🌐 All Shops →</Link>
        <Link to="/citypro/leaderboard" data-testid="cp-goto-leaderboard" className="flex-1 px-3 py-2 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-black text-xs font-black text-center">🏆 Leaderboard →</Link>
        <button onClick={() => setCustomReq(true)} data-testid="cp-custom-request-btn" className="flex-1 px-3 py-2 rounded-xl bg-gradient-to-r from-amber-500 to-yellow-400 text-black text-xs font-black">📩 Custom Request</button>
        <button onClick={() => setShowCart(true)} data-testid="cp-view-cart" className="px-3 py-2 rounded-xl bg-emerald-500 text-black text-xs font-black relative">
          🛒 Cart {cart.length > 0 && <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center font-black">{cart.reduce((s,x)=>s+x.qty,0)}</span>}
        </button>
      </div>
      {!openMerchant && !manageProducts && (
        <>
          <div className="rounded-2xl border border-emerald-400/30 bg-slate-900/60 p-3">
            <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-black mb-2">🏬 Shop Categories · {cats.length}</div>
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-1.5" data-testid="cp-shop-cats">
              {cats.map((c) => (
                <button key={c.id} onClick={() => { setCat(c.id); setSub(''); }} data-testid={`cp-cat-${c.id}`}
                  className={`rounded-xl p-2 flex flex-col items-center gap-0.5 border-2 transition text-center ${cat === c.id ? 'border-emerald-400 bg-emerald-500/20 shadow-lg' : 'border-slate-700 bg-slate-800/60 hover:border-emerald-400/50'}`}>
                  <div className="text-2xl">{c.icon}</div>
                  <div className="text-[9px] font-black leading-tight">{c.name}</div>
                </button>
              ))}
            </div>
          </div>
          {catObj && (
            <div className="rounded-xl bg-slate-900/60 border border-slate-700 p-2">
              <div className="text-[10px] opacity-70 mb-1">Sub-category:</div>
              <div className="flex flex-wrap gap-1">
                <button onClick={() => setSub('')} className={`text-[10px] px-2 py-0.5 rounded ${!sub ? 'bg-emerald-500 text-black' : 'bg-slate-800 text-slate-300'} font-black`}>All</button>
                {(catObj.sub || []).map((s) => (
                  <button key={s} onClick={() => setSub(s)} data-testid={`cp-sub-${s}`} className={`text-[10px] px-2 py-0.5 rounded font-black ${sub === s ? 'bg-emerald-500 text-black' : 'bg-slate-800 text-slate-300'}`}>{s}</button>
                ))}
              </div>
            </div>
          )}
          {cat && promos.length > 0 && (
            <div className="rounded-xl border-2 border-fuchsia-400/50 bg-gradient-to-r from-fuchsia-950/40 to-pink-950/40 p-2" data-testid="cp-promos-strip">
              <div className="text-[10px] uppercase tracking-widest text-fuchsia-300 font-black mb-1">📣 {promos.length} Live Promos in this category</div>
              <div className="flex gap-1.5 overflow-x-auto pb-1">
                {promos.map((p) => (
                  <div key={p.id} className="shrink-0 rounded-lg bg-fuchsia-500/25 border border-fuchsia-400/50 px-2 py-1 text-[10px]" data-testid={`cp-promo-${p.id}`}>
                    <b className="text-fuchsia-200">{p.discount_pct}% OFF</b> · <span className="text-white">{p.shop_name}</span>
                    <div className="opacity-70 text-[9px]">{p.title} · till {fmtDate(p.valid_until)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {cat && (
            <div className="rounded-2xl border border-slate-700 bg-slate-900/60 p-3">
              <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-black mb-2">🛍️ {merchants.length} shops</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {merchants.map((m) => (
                  <button key={m.user_id} onClick={() => openShop(m)} data-testid={`cp-shop-${m.user_id}`}
                    className="rounded-xl border border-emerald-400/30 bg-slate-800/60 hover:bg-emerald-500/10 p-2 text-left transition">
                    <div className="flex items-center gap-2">
                      <div className="text-2xl">{catObj?.icon}</div>
                      <div className="flex-1 min-w-0">
                        <div className="font-black text-sm truncate">{m.shop_name || `@${m.username}`}</div>
                        <div className="text-[10px] opacity-70 truncate">{m.city || 'City'} · {m.product_count || 0} products</div>
                      </div>
                    </div>
                  </button>
                ))}
                {merchants.length === 0 && <div className="text-[11px] opacity-60 text-center py-4 col-span-2">Koi shop nahi mila is category me · Register as merchant in Profile tab!</div>}
              </div>
            </div>
          )}
          {(cfg.profile?.roles || []).includes('merchant') && (
            <button onClick={() => setManageProducts(true)} data-testid="cp-manage-products" className="w-full px-3 py-2 rounded bg-gradient-to-r from-amber-500 to-yellow-400 text-black text-xs font-black">🛠️ Manage My Product Catalog (max 40)</button>
          )}
        </>
      )}

      {openMerchant && (
        <div className="rounded-2xl border-2 border-emerald-400/50 bg-gradient-to-br from-emerald-900/30 to-slate-900 p-3">
          <div className="flex items-center gap-2 mb-2">
            <button onClick={() => { setOpenMerchant(null); setProducts([]); }} className="text-xs text-slate-400">← Back</button>
            <div className="flex-1 text-sm font-black">{openMerchant.shop_name || `@${openMerchant.username}`}</div>
            {openMerchant.phone && <a href={`tel:${openMerchant.phone}`} className="text-[10px] px-2 py-0.5 rounded bg-emerald-500/25 text-emerald-200 font-black">📞 Call</a>}
          </div>
          <div className="text-[10px] opacity-70 mb-2">{openMerchant.city} · {(openMerchant.merchant_subcategories || []).join(' · ')}</div>
          <div className="grid grid-cols-2 gap-2">
            {products.map((p) => (
              <div key={p.id} className="rounded-xl border border-slate-700 bg-slate-800/60 p-2" data-testid={`cp-prod-${p.id}`}>
                {p.image_url && <div className="aspect-square rounded bg-slate-900 mb-1 bg-cover bg-center" style={{ backgroundImage: `url(${p.image_url})` }} />}
                <div className="text-[11px] font-black truncate">{p.name}</div>
                <div className="text-[9px] opacity-70">{p.subcategory}</div>
                <div className="text-sm font-black text-emerald-300 mt-0.5">{p.price_at} AT</div>
                {!isMyShop && (
                  <div className="flex gap-1 mt-1">
                    <button onClick={() => addToCart(openMerchant, p)} data-testid={`cp-add-cart-${p.id}`} className="flex-1 px-1.5 py-1 rounded bg-emerald-500 text-black text-[9px] font-black">🛒 Add</button>
                    <button onClick={() => { addToCart(openMerchant, p); setShowCart(true); }} data-testid={`cp-buy-now-${p.id}`} className="flex-1 px-1.5 py-1 rounded bg-amber-500 text-black text-[9px] font-black">⚡ Buy</button>
                    <button onClick={() => shareProduct(openMerchant, p)} data-testid={`cp-share-${p.id}`} className="px-1.5 py-1 rounded bg-cyan-500/25 text-cyan-200 text-[9px] font-black">↗</button>
                  </div>
                )}
              </div>
            ))}
            {products.length === 0 && <div className="text-[11px] opacity-60 text-center py-4 col-span-2">Iss shop me abhi products nahi hain</div>}
          </div>
          {!isMyShop && <div className="text-[10px] opacity-70 mt-3 text-center">🛒 Add to cart karo · Checkout par delivery charges dikhenge</div>}
        </div>
      )}

      {manageProducts && <ProductManager onClose={() => setManageProducts(false)} />}
      {showCart && <CartCheckoutModal cart={cart} saveCart={saveCart} onClose={() => setShowCart(false)} />}
      {customReq && <CustomRequestModal onClose={() => setCustomReq(false)} />}
    </div>
  );
}

// ============ ORDER BOOK — customer's all orders with full status flow ============
function OrderBookTab() {
  const [orders, setOrders] = useState([]);
  const [role, setRole] = useState('customer');
  const [filter, setFilter] = useState('');
  const load = useCallback(async () => {
    try { const r = await api.get('/citypro/orders', { params: { role, limit: 100 } }); setOrders(r.data?.orders || []); }
    catch { /* silent */ }
  }, [role]);
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, [load]);
  const statusMeta = {
    booked:      { emoji: '📝', color: 'bg-slate-500/25 text-slate-200' },
    pending:     { emoji: '⏳', color: 'bg-amber-500/25 text-amber-200' },
    approved:    { emoji: '✅', color: 'bg-cyan-500/25 text-cyan-200' },
    accepted:    { emoji: '👍', color: 'bg-cyan-500/25 text-cyan-200' },
    rejected:    { emoji: '⛔', color: 'bg-red-500/25 text-red-200' },
    processing:  { emoji: '⚙️', color: 'bg-violet-500/25 text-violet-200' },
    on_the_way:  { emoji: '🛵', color: 'bg-blue-500/25 text-blue-200' },
    in_progress: { emoji: '🛰️', color: 'bg-blue-500/25 text-blue-200' },
    delivered:   { emoji: '📦', color: 'bg-emerald-500/25 text-emerald-200' },
    completed:   { emoji: '🏁', color: 'bg-emerald-500/25 text-emerald-200' },
    cancelled:   { emoji: '❌', color: 'bg-rose-500/25 text-rose-200' },
  };
  const filtered = filter ? orders.filter((o) => o.status === filter) : orders;
  const byStatus = filtered.reduce((a, o) => { (a[o.status] = a[o.status] || []).push(o); return a; }, {});
  return (
    <div className="space-y-3" data-testid="cp-orderbook-tab">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex rounded-lg overflow-hidden">
          <button onClick={() => setRole('customer')} data-testid="ob-role-customer" className={`px-2 py-1 text-[11px] font-black ${role === 'customer' ? 'bg-emerald-500 text-black' : 'bg-slate-800 text-slate-300'}`}>🛒 My Orders</button>
          <button onClick={() => setRole('provider')} data-testid="ob-role-provider" className={`px-2 py-1 text-[11px] font-black ${role === 'provider' ? 'bg-emerald-500 text-black' : 'bg-slate-800 text-slate-300'}`}>🚕 Provider Dashboard</button>
        </div>
        <select value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="ob-filter" className="px-2 py-1 rounded bg-slate-800 border border-slate-700 text-xs">
          <option value="">— All statuses ({orders.length}) —</option>
          {Object.keys(statusMeta).map((s) => <option key={s} value={s}>{statusMeta[s].emoji} {s} ({orders.filter((o) => o.status === s).length})</option>)}
        </select>
        <button onClick={load} className="ml-auto text-[10px] px-2 py-1 rounded bg-white/10">↻ Refresh</button>
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-1">
        {Object.entries(statusMeta).map(([s, m]) => (
          <div key={s} className={`rounded-lg text-center px-1.5 py-1 text-[9px] font-black ${m.color}`} data-testid={`ob-count-${s}`}>
            <div className="text-lg">{m.emoji}</div>
            <div className="opacity-80 uppercase">{s.replace('_',' ')}</div>
            <div className="text-sm">{(byStatus[s] || []).length}</div>
          </div>
        ))}
      </div>
      <div className="space-y-1">
        {filtered.map((o) => (
          <div key={o.id} className="rounded bg-slate-800/60 p-2 text-[11px]" data-testid={`ob-order-${o.id}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <b className="flex-1 truncate">{o.service_name}</b>
              <span className={`text-[9px] px-1.5 py-0.5 rounded font-black ${statusMeta[o.status]?.color || 'bg-slate-600 text-white'}`}>{statusMeta[o.status]?.emoji} {o.status}</span>
              <span className="text-emerald-300 font-black">{o.amount_at} AT</span>
            </div>
            <div className="text-[10px] opacity-70 mt-0.5">
              {o.pickup_addr && <span>📍 {o.pickup_addr.slice(0, 30)} </span>}
              {o.drop_addr && <span>→ {o.drop_addr.slice(0, 30)} </span>}
              · {fmtDateTime(o.created_at)}
              {role === 'customer' && o.provider_username && <span> · 👤 @{o.provider_username}</span>}
              {role === 'provider' && <span> · 🛒 @{o.customer_username}</span>}
            </div>
            {['approved', 'accepted', 'processing', 'on_the_way', 'in_progress'].includes(o.status) && <TripTimeline order={o} />}
          </div>
        ))}
        {filtered.length === 0 && <div className="text-center opacity-60 py-6 text-xs">No orders in this filter</div>}
      </div>
    </div>
  );
}

// ============ DELIVERY PARTNERS TAB — filters + WA group invite + status ============
function DeliveryPartnersTab({ cfg }) {
  const [partners, setPartners] = useState([]);
  const [area, setArea] = useState('');
  const [speed, setSpeed] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const groupLink = cfg.wa_group_link || '';
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (area) params.area = area;
      if (speed) params.speed = speed;
      if (status) params.status = status;
      const r = await api.get('/citypro/delivery-partners', { params });
      setPartners(r.data?.partners || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [area, speed, status]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-3" data-testid="cp-partners-tab">
      {groupLink && (
        <a href={groupLink} target="_blank" rel="noreferrer" data-testid="cp-wa-group-link"
           className="block w-full px-3 py-2.5 rounded-xl bg-gradient-to-r from-green-500 via-emerald-500 to-teal-500 text-black text-sm font-black text-center">
          👥 Delivery Partner WhatsApp Group · Join Now →
        </a>
      )}
      <div className="grid grid-cols-3 gap-2">
        <input placeholder="🌆 Area / City" value={area} onChange={(e) => setArea(e.target.value)} data-testid="cp-partner-area" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
        <select value={speed} onChange={(e) => setSpeed(e.target.value)} data-testid="cp-partner-speed" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs">
          <option value="">Any speed</option>
          <option value="fast">⚡ Fast</option>
          <option value="super_fast">🚀 Super Fast</option>
          <option value="standard">Standard</option>
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="cp-partner-status" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs">
          <option value="">Any status</option>
          <option value="available">🟢 Available</option>
          <option value="ready">✅ Ready to Deliver</option>
          <option value="not_available">🔴 Not Available</option>
        </select>
      </div>
      <div className="rounded-2xl border border-slate-700 bg-slate-900/60 p-3">
        <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-black mb-2">🛵 {partners.length} delivery partners</div>
        {loading && <div className="text-center opacity-70 py-3 text-xs">⏳ Loading…</div>}
        <div className="space-y-1">
          {partners.map((p) => (
            <div key={p.user_id} className="flex items-center gap-2 rounded bg-slate-800/60 p-2 text-[11px]" data-testid={`cp-partner-${p.user_id}`}>
              <div className="w-9 h-9 rounded-full bg-emerald-500/20 flex items-center justify-center text-lg">🛵</div>
              <div className="flex-1 min-w-0">
                <div className="font-black truncate">@{p.username} <span className="text-[9px] opacity-70 ml-1">{p.city || 'city n/a'}</span></div>
                <div className="text-[9px] opacity-70">
                  {p.speed_tier === 'super_fast' ? '🚀 Super Fast' : p.speed_tier === 'fast' ? '⚡ Fast' : 'Standard'}
                  {p.rating_avg ? ` · ⭐${p.rating_avg} (${p.rating_count || 0})` : ' · new'}
                </div>
              </div>
              <span className={`text-[9px] px-2 py-0.5 rounded font-black ${p.availability === 'not_available' || !p.active ? 'bg-red-500/25 text-red-200' : p.availability === 'ready' ? 'bg-emerald-500/25 text-emerald-200' : 'bg-cyan-500/25 text-cyan-200'}`}>
                {p.availability === 'not_available' || !p.active ? 'not available' : p.availability === 'ready' ? 'ready' : 'available'}
              </span>
              {p.phone && <a href={`tel:${p.phone}`} className="text-[10px] px-2 py-0.5 rounded bg-slate-700 text-slate-200">📞</a>}
              {p.whatsapp_number && <a href={`https://wa.me/${p.whatsapp_number.replace(/[^\d]/g,'')}`} target="_blank" rel="noreferrer" className="text-[10px] px-2 py-0.5 rounded bg-green-600 text-white">💬</a>}
            </div>
          ))}
          {partners.length === 0 && !loading && <div className="text-center opacity-60 py-4 text-xs">No partners match these filters</div>}
        </div>
      </div>
    </div>
  );
}

// ============ MERCHANT ADS WALLET — buy boost to jump higher in showcase ============
function MerchantBoostCard({ onReload }) {
  const [data, setData] = useState({ packs: [], current_boost_score: 0, boost_expires_at: null });
  const [busy, setBusy] = useState('');
  const load = async () => {
    try { const r = await api.get('/citypro/boost/packs'); setData(r.data); } catch { /* silent */ }
  };
  useEffect(() => { load(); }, []);
  const buy = async (id, label, at) => {
    if (!window.confirm(`Buy ${label}? ${at} AT will be debited from your wallet.`)) return;
    setBusy(id);
    try {
      const r = await api.post('/citypro/boost/buy', { pack_id: id });
      toast.success(`🚀 Boost active · score ${r.data.boost_score}`);
      load(); onReload?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Boost failed'); }
    finally { setBusy(''); }
  };
  const exp = data.boost_expires_at ? new Date(data.boost_expires_at) : null;
  const expLeftMs = exp ? exp - new Date() : 0;
  const daysLeft = Math.max(0, Math.ceil(expLeftMs / 86400000));
  return (
    <div className="rounded-2xl border-2 border-orange-400/50 bg-gradient-to-br from-orange-950/40 to-slate-900 p-3" data-testid="cp-boost-card">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-widest text-orange-300 font-black">🚀 Merchant Ads Wallet · Boost Showcase Position</div>
        <div className="text-[10px] font-black text-orange-200">Score: <span className="text-lg">{data.current_boost_score || 0}</span>{daysLeft > 0 && <span className="ml-1 opacity-80">· {daysLeft}d left</span>}</div>
      </div>
      <div className="text-[10px] opacity-80 mb-2">Higher score = higher position on <b>/citypro/showcase</b> grid. Score stacks on re-purchase, expires with the pack duration. Full AT flows into 7-tier cascade.</div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {(data.packs || []).map((p) => (
          <button key={p.id} onClick={() => buy(p.id, p.label, p.at)} disabled={busy === p.id} data-testid={`cp-boost-${p.id}`}
                  className="rounded-xl border-2 border-orange-400/40 bg-slate-800/60 hover:border-orange-400 hover:bg-orange-500/10 p-2 text-left transition disabled:opacity-40">
            <div className="text-xs font-black text-orange-200">{p.label}</div>
            <div className="text-lg font-black text-orange-100 mt-0.5">{p.at} AT</div>
            <div className="text-[10px] opacity-80">+{p.score} score · {p.days}d</div>
            <div className="text-[10px] text-orange-300 font-black mt-1">{busy === p.id ? '⏳' : 'Buy Boost →'}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ============ CART + CHECKOUT ============
function CartCheckoutModal({ cart, saveCart, onClose }) {
  const [deliveryFee, setDeliveryFee] = useState(20);
  const [addr, setAddr] = useState('');
  const [busy, setBusy] = useState(false);
  const subtotal = cart.reduce((s, x) => s + x.price_at * x.qty, 0);
  const total = subtotal + Number(deliveryFee || 0);
  const changeQty = (id, d) => {
    const next = cart.map((x) => x.id === id ? { ...x, qty: Math.max(0, x.qty + d) } : x).filter((x) => x.qty > 0);
    saveCart(next);
  };
  const checkout = async () => {
    if (cart.length === 0) { toast.error('Cart empty'); return; }
    if (!addr) { toast.error('Delivery address bharo'); return; }
    setBusy(true);
    try {
      const notes = 'Cart: ' + cart.map((x) => `${x.name} x${x.qty}`).join(' · ');
      // Uses generic retail order with total price + delivery fee going to merchant
      const r = await api.post('/citypro/orders', {
        service_id: 'retail', provider_id: cart[0]?.merchant_id || null,
        merchant_id: cart[0]?.merchant_id || null,
        cart_total_at: subtotal,
        pickup_addr: addr, notes, delivery_fee_at: Number(deliveryFee || 0),
        payload: { cart, subtotal }, offline_id: crypto.randomUUID?.() || String(Date.now()),
      });
      toast.success(`✓ Order placed · ${r.data.order.amount_at} AT held`);
      saveCart([]); onClose();
      window.dispatchEvent(new CustomEvent('citypro-refresh'));
    } catch (e) { toast.error(e?.response?.data?.detail || 'Checkout failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] bg-black/85 flex items-center justify-center p-3 overflow-y-auto" onClick={onClose} data-testid="cp-cart-modal">
      <div className="bg-slate-900 border-2 border-emerald-400 rounded-2xl p-4 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs font-black text-emerald-300 uppercase tracking-widest">🛒 Your Cart · {cart.length} items</div>
          <button onClick={onClose} className="text-slate-400 text-lg">✕</button>
        </div>
        <div className="max-h-64 overflow-y-auto space-y-1">
          {cart.map((x) => (
            <div key={x.id} className="flex items-center gap-2 bg-slate-800/60 rounded p-2 text-[11px]">
              <div className="flex-1 truncate"><b>{x.name}</b> <span className="opacity-70">@ {x.merchant_name}</span></div>
              <div className="flex items-center gap-1">
                <button onClick={() => changeQty(x.id, -1)} data-testid={`cart-dec-${x.id}`} className="w-6 h-6 rounded bg-slate-700 text-white font-black">−</button>
                <span className="font-black w-4 text-center">{x.qty}</span>
                <button onClick={() => changeQty(x.id, +1)} data-testid={`cart-inc-${x.id}`} className="w-6 h-6 rounded bg-slate-700 text-white font-black">+</button>
              </div>
              <div className="w-16 text-right font-black text-emerald-300">{x.price_at * x.qty} AT</div>
            </div>
          ))}
          {cart.length === 0 && <div className="text-xs opacity-60 text-center py-4">Cart empty</div>}
        </div>
        <div className="mt-2 space-y-2">
          <label className="text-[10px] flex flex-col">
            <span className="opacity-80 mb-0.5">Delivery Address</span>
            <input value={addr} onChange={(e) => setAddr(e.target.value)} data-testid="cart-addr" placeholder="Full address with landmark" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          </label>
          <label className="text-[10px] flex flex-col">
            <span className="opacity-80 mb-0.5">Custom Delivery Charge (AT)</span>
            <input type="number" min={0} value={deliveryFee} onChange={(e) => setDeliveryFee(e.target.value)} data-testid="cart-dfee" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          </label>
        </div>
        <div className="mt-2 flex justify-between text-xs font-black">
          <span>Subtotal</span><span className="text-emerald-300">{subtotal} AT</span>
        </div>
        <div className="flex justify-between text-xs font-black">
          <span>Delivery</span><span className="text-emerald-300">{Number(deliveryFee || 0)} AT</span>
        </div>
        <div className="flex justify-between text-sm font-black mt-1 pt-1 border-t border-slate-700">
          <span>Total</span><span className="text-emerald-200">{total} AT</span>
        </div>
        <button onClick={checkout} disabled={busy || cart.length === 0} data-testid="cart-checkout"
                className="w-full mt-3 px-3 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-500 text-black text-sm font-black disabled:opacity-40">
          {busy ? '⏳ Placing…' : `✓ Confirm Order · Pay ${total} AT`}
        </button>
      </div>
    </div>
  );
}

// ============ CUSTOM PRODUCT/SERVICE REQUEST → WhatsApp ============
function CustomRequestModal({ onClose }) {
  const [form, setForm] = useState({ kind: 'product', title: '', description: '', budget_at: '', city: '' });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!form.title || !form.description) { toast.error('Title + description bharo'); return; }
    setBusy(true);
    try {
      const r = await api.post('/citypro/custom-request', { ...form, budget_at: form.budget_at ? parseFloat(form.budget_at) : null });
      toast.success('✓ Request logged');
      if (r.data?.whatsapp_url) window.open(r.data.whatsapp_url, '_blank');
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] bg-black/85 flex items-center justify-center p-3" onClick={onClose} data-testid="cp-custom-req-modal">
      <div className="bg-slate-900 border-2 border-amber-400 rounded-2xl p-4 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="text-xs font-black text-amber-300 uppercase tracking-widest mb-3">📩 Custom Product / Service Request</div>
        <div className="space-y-2">
          <div className="flex gap-1">
            {['product', 'service'].map((k) => (
              <button key={k} onClick={() => setForm({ ...form, kind: k })} data-testid={`cp-cr-kind-${k}`} className={`flex-1 px-2 py-1 rounded text-[11px] font-black ${form.kind === k ? 'bg-amber-500 text-black' : 'bg-slate-800 text-slate-300'}`}>{k === 'product' ? '📦 Product' : '🛠️ Service'}</button>
            ))}
          </div>
          <input placeholder="Title (short)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="cp-cr-title" className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          <textarea rows={4} placeholder="Detailed description of what you need" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="cp-cr-desc" className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          <div className="grid grid-cols-2 gap-2">
            <input placeholder="Budget in AT" type="number" value={form.budget_at} onChange={(e) => setForm({ ...form, budget_at: e.target.value })} data-testid="cp-cr-budget" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
            <input placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="cp-cr-city" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          </div>
          <button onClick={submit} disabled={busy} data-testid="cp-cr-submit" className="w-full mt-1 px-3 py-2 rounded-xl bg-gradient-to-r from-amber-500 to-yellow-400 text-black text-sm font-black disabled:opacity-40">
            {busy ? '⏳' : '📱 Send Request on WhatsApp'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ProductManager({ onClose }) {
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const empty = { name: '', price_at: '', subcategory: '', description: '', image_url: '', stock: '', active: true };
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [csvText, setCsvText] = useState('');
  const [showPromo, setShowPromo] = useState(false);
  const [promo, setPromo] = useState({ active: true, discount_pct: 10, title: 'Weekly Special', valid_days: 7 });
  const load = useCallback(async () => {
    const r = await api.get('/citypro/products', { params: { limit: 100 } });
    setProducts(r.data?.products || []);
  }, []);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    if (!form.name || !form.price_at) { toast.error('Name + Price required'); return; }
    setBusy(true);
    try {
      const body = { ...form, price_at: parseFloat(form.price_at), stock: form.stock ? parseInt(form.stock) : null };
      if (editing) await api.patch(`/citypro/products/${editing}`, body);
      else await api.post('/citypro/products', body);
      toast.success(editing ? '✓ Updated' : '✓ Product added');
      setForm(empty); setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  const del = async (id) => {
    if (!window.confirm('Delete product?')) return;
    try { await api.delete(`/citypro/products/${id}`); toast.success('Deleted'); load(); }
    catch { toast.error('Failed'); }
  };

  const parseCsv = () => {
    // CSV format: name,price_at,subcategory,description,image_url,stock  (first row = header optional)
    const lines = csvText.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    if (lines.length === 0) return [];
    const first = lines[0].toLowerCase();
    const startIdx = first.includes('name') && first.includes('price') ? 1 : 0;
    const out = [];
    for (let i = startIdx; i < lines.length; i++) {
      // simple CSV — commas inside quotes handled minimally
      const parts = lines[i].match(/("[^"]*"|[^,]+)/g)?.map((s) => s.replace(/^"|"$/g, '').trim()) || [];
      if (parts.length < 2) continue;
      const [name, price_at, subcategory, description, image_url, stock] = parts;
      const price = parseFloat(price_at);
      if (!name || !(price >= 0)) continue;
      out.push({
        name: name.slice(0, 120), price_at: price,
        subcategory: subcategory || null, description: description || null,
        image_url: image_url || null, stock: stock ? parseInt(stock) || null : null,
        active: true,
      });
    }
    return out;
  };
  const bulkImport = async () => {
    const items = parseCsv();
    if (items.length === 0) { toast.error('No valid rows found'); return; }
    setBusy(true);
    try {
      const r = await api.post('/citypro/products/bulk-import', { products: items });
      toast.success(`✓ Imported ${r.data.inserted}${r.data.skipped ? ` · skipped ${r.data.skipped}` : ''}`);
      setCsvText(''); setShowBulk(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Import failed'); }
    finally { setBusy(false); }
  };

  const savePromo = async () => {
    setBusy(true);
    try { await api.post('/citypro/promos', promo); toast.success('✓ Promo saved · broadcasted'); setShowPromo(false); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border-2 border-amber-400/50 bg-slate-900 p-3" data-testid="cp-product-mgr">
      <div className="flex items-center gap-2 mb-3">
        <button onClick={onClose} className="text-xs text-slate-400">← Back</button>
        <div className="flex-1 text-xs font-black text-amber-300 uppercase tracking-widest">🛠️ My Products · {products.length}/40</div>
        <button onClick={() => setShowBulk(!showBulk)} data-testid="cp-bulk-toggle" className="text-[10px] px-2 py-1 rounded bg-cyan-500 text-black font-black">📥 Bulk CSV</button>
        <button onClick={() => setShowPromo(!showPromo)} data-testid="cp-promo-toggle" className="text-[10px] px-2 py-1 rounded bg-fuchsia-500 text-white font-black">📣 Promo</button>
      </div>

      {showPromo && (
        <div className="rounded-xl border border-fuchsia-400/50 bg-fuchsia-500/10 p-2 mb-3" data-testid="cp-promo-box">
          <div className="text-[10px] uppercase tracking-widest text-fuchsia-300 font-black mb-1">📣 Weekly Promo Broadcast (nearby customers in your category)</div>
          <div className="grid grid-cols-2 gap-1 mb-2">
            <input placeholder="Title" value={promo.title} onChange={(e) => setPromo({ ...promo, title: e.target.value })} data-testid="cp-promo-title" className="col-span-2 px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
            <label className="flex flex-col text-[10px]">
              <span>Discount %</span>
              <input type="number" value={promo.discount_pct} onChange={(e) => setPromo({ ...promo, discount_pct: parseInt(e.target.value) || 0 })} data-testid="cp-promo-pct" className="px-2 py-1 rounded bg-slate-800 border border-slate-700 text-xs" />
            </label>
            <label className="flex flex-col text-[10px]">
              <span>Valid days</span>
              <input type="number" value={promo.valid_days} onChange={(e) => setPromo({ ...promo, valid_days: parseInt(e.target.value) || 1 })} className="px-2 py-1 rounded bg-slate-800 border border-slate-700 text-xs" />
            </label>
          </div>
          <label className="flex items-center gap-1 text-[11px] mb-2"><input type="checkbox" checked={promo.active} onChange={(e) => setPromo({ ...promo, active: e.target.checked })} data-testid="cp-promo-active" /> Broadcast active</label>
          <button onClick={savePromo} disabled={busy} data-testid="cp-promo-save" className="w-full px-3 py-1.5 rounded bg-gradient-to-r from-fuchsia-500 to-pink-500 text-white text-xs font-black">📣 Save & Broadcast</button>
        </div>
      )}

      {showBulk && (
        <div className="rounded-xl border border-cyan-400/50 bg-cyan-500/10 p-2 mb-3" data-testid="cp-bulk-box">
          <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-black mb-1">📥 Bulk CSV Import (max 40 rows)</div>
          <div className="text-[10px] opacity-80 mb-1">Format: <code>name, price_at, subcategory, description, image_url, stock</code> (header row optional)</div>
          <textarea rows={5} value={csvText} onChange={(e) => setCsvText(e.target.value)} data-testid="cp-csv-text" placeholder={'Redmi Note 13,15000,Smartphones,6GB/128GB,https://...,10\nCharger,299,Accessories,Type-C fast,https://...,50'} className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-[10px] font-mono mb-2" />
          <div className="flex gap-2">
            <label className="flex-1 px-3 py-1.5 rounded bg-slate-700 text-slate-200 text-xs font-black text-center cursor-pointer">
              📄 Upload CSV file
              <input type="file" accept=".csv,text/csv" onChange={(e) => { const f = e.target.files?.[0]; if (!f) return; const fr = new FileReader(); fr.onload = () => setCsvText(String(fr.result || '')); fr.readAsText(f); }} data-testid="cp-csv-file" className="hidden" />
            </label>
            <button onClick={bulkImport} disabled={busy || !csvText.trim()} data-testid="cp-csv-import" className="flex-1 px-3 py-1.5 rounded bg-cyan-500 text-black text-xs font-black disabled:opacity-40">✓ Import Rows</button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-2 mb-2">
        <input placeholder="Product name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="cp-p-name" className="col-span-2 px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
        <input type="number" placeholder="Price (AT)" value={form.price_at} onChange={(e) => setForm({ ...form, price_at: e.target.value })} data-testid="cp-p-price" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
        <input placeholder="Subcategory" value={form.subcategory} onChange={(e) => setForm({ ...form, subcategory: e.target.value })} className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
        <input placeholder="Image URL" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className="col-span-2 px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
        <input type="number" placeholder="Stock (opt.)" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
        <label className="flex items-center gap-1 text-[11px] px-2 py-1.5"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Active</label>
        <textarea rows={2} placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="col-span-2 px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
      </div>
      <div className="flex gap-2 mb-3">
        <button onClick={save} disabled={busy} data-testid="cp-p-save" className="flex-1 px-3 py-2 rounded bg-amber-500 text-black text-xs font-black">{editing ? '💾 Update' : '➕ Add Product'}</button>
        {editing && <button onClick={() => { setForm(empty); setEditing(null); }} className="px-3 py-2 rounded bg-slate-700 text-slate-200 text-xs">Cancel</button>}
      </div>
      <div className="grid grid-cols-2 gap-1 max-h-80 overflow-y-auto">
        {products.map((p) => (
          <div key={p.id} className="rounded bg-slate-800/60 p-2 text-[11px]" data-testid={`cp-mp-${p.id}`}>
            {p.image_url && <div className="aspect-square rounded bg-slate-900 mb-1 bg-cover bg-center" style={{ backgroundImage: `url(${p.image_url})` }} />}
            <div className="font-black truncate">{p.name}</div>
            <div className="text-[10px] text-emerald-300 font-black">{p.price_at} AT</div>
            <div className="flex gap-1 mt-1">
              <button onClick={() => { setEditing(p.id); setForm({ ...p, price_at: String(p.price_at), stock: p.stock ?? '' }); }} className="flex-1 px-1.5 py-0.5 rounded bg-slate-700 text-slate-200 text-[9px]">✏️</button>
              <button onClick={() => del(p.id)} className="px-1.5 py-0.5 rounded bg-red-500/30 text-red-300 text-[9px]">✕</button>
            </div>
          </div>
        ))}
        {products.length === 0 && <div className="col-span-2 text-[11px] opacity-60 text-center py-4">No products yet</div>}
      </div>
    </div>
  );
}

// ============ WHATSAPP ORDER BOT TAB ============
function WhatsAppBotTab() {
  const [text, setText] = useState('TAXI from Sector 5 to Airport 12km');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const parse = async (confirm = false) => {
    setBusy(true);
    try {
      const r = await api.post('/citypro/wa/parse', { text, confirm });
      if (confirm && r.data?.created) { toast.success(r.data.reply); setPreview(null); }
      else setPreview(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  const waLink = () => {
    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank');
  };
  return (
    <div className="space-y-3" data-testid="cp-wabot-tab">
      <div className="rounded-2xl border-2 border-green-400/50 bg-gradient-to-br from-green-950/40 to-slate-900 p-3">
        <div className="text-[10px] uppercase tracking-widest text-green-300 font-black mb-2">💬 WhatsApp Order Bot · Site khole bina book karo</div>
        <div className="text-[11px] opacity-90 mb-2">
          Bhai, apna WhatsApp DM se bhi book kar sakte ho. Example messages:<br />
          • <b>TAXI from Home to Airport 12km</b><br />
          • <b>LAUNDRY pickup 10am</b><br />
          • <b>GROCERY urgent · Sector 5</b><br />
          • <b>DOCTOR appointment please</b>
        </div>
        <textarea rows={3} value={text} onChange={(e) => setText(e.target.value)} data-testid="cp-wa-text" placeholder="Type your booking message…" className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs mb-2" />
        <div className="grid grid-cols-2 gap-2">
          <button onClick={() => parse(false)} disabled={busy} data-testid="cp-wa-preview" className="px-3 py-2 rounded bg-cyan-500 text-black text-xs font-black">👀 Preview Order</button>
          <button onClick={() => parse(true)} disabled={busy} data-testid="cp-wa-confirm" className="px-3 py-2 rounded bg-gradient-to-r from-green-500 to-emerald-500 text-white text-xs font-black">✅ Confirm & Book</button>
        </div>
        {preview && (
          <div className="mt-2 rounded-lg bg-slate-800/60 border border-green-400/30 p-2 text-[11px]" data-testid="cp-wa-preview-box">
            <pre className="whitespace-pre-wrap font-mono text-[10px] opacity-90">{preview.reply}</pre>
            {preview.preview && (
              <div className="mt-1 text-[10px] opacity-80">
                {preview.preview.icon} {preview.preview.service_name} · <b className="text-emerald-300">{preview.preview.amount_at} AT</b>
                {preview.preview.pickup_addr && <> · 📍 {preview.preview.pickup_addr}</>}
                {preview.preview.drop_addr && <> → {preview.preview.drop_addr}</>}
              </div>
            )}
          </div>
        )}
        <div className="mt-3 border-t border-slate-700 pt-2">
          <div className="text-[10px] opacity-80 mb-1">Ya WhatsApp me forward karke bhejein:</div>
          <button onClick={waLink} data-testid="cp-wa-share" className="w-full px-3 py-2 rounded bg-green-500 text-white text-xs font-black">📱 Send via WhatsApp App</button>
        </div>
      </div>
      <div className="rounded-xl border border-amber-400/30 bg-amber-500/5 p-2 text-[10px] opacity-90">
        <b className="text-amber-300">Bot Coming Soon:</b> Real WhatsApp Business API bot (Baileys) jald activate hoga — tab yeh messages seedhe WhatsApp me hi bhejenge aur bot reply karega. Abhi ye endpoint browser se test kar sakte ho.
      </div>
    </div>
  );
}

